import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import {
  House, Users, Calculator, Receipt, ChartLine, Gear, Plus,
  CaretRight, Check, X, PencilSimple, ArrowsClockwise,
  CurrencyDollar, Storefront, FileText, Clock, CalendarBlank,
  CurrencyCircleDollar, Buildings, Timer, Money, CreditCard,
  Scales, Eye, UserCircle, SignOut, Brain, EnvelopeSimple, Sparkle, Broom, Trash, List, Sun, Moon
} from "@phosphor-icons/react";
import RulesConfiguration from "@/components/RulesConfiguration";
import BillingCalculator from "@/components/BillingCalculator";
import AdSpendEntry from "@/components/AdSpendEntry";
import ClientMonthlyView from "@/components/ClientMonthlyView";
import DailyRevenueCharts from "@/components/DailyRevenueCharts";
import PrivacyPolicy from "@/components/PrivacyPolicy";
import ClientFacebookConnection from "@/components/ClientFacebookConnection";
import ClientGoogleAnalytics from "@/components/ClientGoogleAnalytics";
import ClientGoogleAds from "@/components/ClientGoogleAds";
import ClientSearchConsole from "@/components/ClientSearchConsole";
import ShopifyConnection from "@/components/ShopifyConnection";
import BillingTrends from "@/components/BillingTrends";
import ClientAdCampaigns from "@/components/ClientAdCampaigns";
import PerformanceIntelligence from "@/components/intelligence/PerformanceIntelligence";
import DailyRevenueByClient from "@/components/DailyRevenueByClient";
import BillingConfigPanel from "@/components/BillingConfigPanel";
import BrandingEditor from "@/components/BrandingEditor";
import SpendMapping from "@/components/SpendMapping";
import DiBrandFooter from "@/components/DiBrandFooter";
import { MonthProvider, useMonth } from "@/context/MonthContext";
import { AuthProvider, useAuth } from "@/AuthContext";
import { BrandProvider, useBrand } from "@/BrandContext";
import { ThemeProvider, useTheme } from "@/ThemeContext";
import LoginPage from "@/components/LoginPage";
import { RIOSDashboard, ContactsList, ContactProfile, CSVImport, CompaniesList, BrandsList } from "@/components/RIOS";
import "@/index.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = false;

const api = {
  get: (url) => axios.get(`${API}${url}`),
  post: (url, data) => axios.post(`${API}${url}`, data),
  put: (url, data) => axios.put(`${API}${url}`, data),
  delete: (url) => axios.delete(`${API}${url}`),
};

const formatCurrency = (amount, currency = "INR") => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 2,
  }).format(amount || 0);
};

const timeAgo = (iso) => {
  if (!iso) return "—";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} d ago`;
};

const REVENUE_BASIS_LABELS = {
  gross: "Gross Revenue",
  minus_tax: "Revenue - Tax",
  minus_tax_shipping: "Revenue - Tax & Shipping",
  principal: "Principal Only",
};

// ==================== SIDEBAR ====================
const ThemeToggle = ({ compact = false }) => {
  const { theme, toggleTheme } = useTheme();
  const isSoft = theme === "soft";
  const Icon = isSoft ? Sun : Moon;
  const label = isSoft ? "Soft" : "Stark";
  if (compact) {
    return (
      <button
        type="button"
        onClick={toggleTheme}
        className="text-white/70 hover:text-white p-1.5"
        title={isSoft ? "Switch to stark dark" : "Switch to soft dark"}
        data-testid="theme-toggle-mobile"
      >
        <Icon size={20} weight={isSoft ? "fill" : "regular"} />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={toggleTheme}
      className="w-full flex items-center justify-between px-4 py-2 text-xs text-white/70 hover:bg-white/5 hover:text-white transition-colors"
      data-testid="theme-toggle"
    >
      <span className="flex items-center gap-2">
        <Icon size={16} weight={isSoft ? "fill" : "regular"} />
        <span>{isSoft ? "Soft Dark" : "Stark Dark"}</span>
      </span>
      <span className="text-[10px] text-white/40">click to switch</span>
    </button>
  );
};

const Sidebar = ({ mobileOpen, onClose }) => {
  const location = useLocation();
  const { selectedMonth, setSelectedMonth, monthOptions } = useMonth();
  const { user, logout, hasPermission } = useAuth();
  const { branding } = useBrand();
  const isClientUser = user?.role === "client_user";
  const isAdminUser = user?.role === "admin_user";
  // client_user is a strictly-scoped read-only client portal — they ONLY see
  // their assigned clients via the Dashboard + Clients pages. NO billing,
  // invoices, ad-spend, settings or admin nav.
  // admin_user is an operational Performance role — same nav scope as
  // client_user (Dashboard + Clients only) but can WRITE integrations and
  // spend mapping for their assigned clients.
  const allLinks = [
    { path: "/", icon: House, label: "Dashboard" },
    { path: "/clients", icon: Users, label: "Clients" },
    { path: "/ad-spend", icon: CurrencyCircleDollar, label: "Ad Spend" },
    { path: "/billing", icon: Calculator, label: "Billing" },
    { path: "/billing-trends", icon: ChartLine, label: "Billing Trends" },
    { path: "/invoices", icon: Receipt, label: "Invoices" },
    { path: "/settings", icon: Gear, label: "Settings" },
  ];
  const links = (isClientUser || isAdminUser)
    ? allLinks.filter(l => l.path === "/" || l.path === "/clients")
    : allLinks;
  return (
    <aside className={`sidebar ${mobileOpen ? "mobile-open" : ""}`} data-testid="sidebar">
      <div className="px-4 py-4 border-b border-white/10 flex items-center justify-between">
        {branding.logo_base64 ? (
          <img src={branding.logo_base64} alt={branding.portal_title || "Logo"} className="max-w-[180px] max-h-[56px] w-auto h-auto object-contain" data-testid="sidebar-brand-logo" />
        ) : (
          <img src="/digital-impressions-logo.png" alt={branding.portal_title || "Digital Impressions"} className="w-[180px] h-auto" data-testid="sidebar-brand-logo" />
        )}
        {/* Close button visible only on mobile via CSS (hidden ≥ md) */}
        <button
          onClick={onClose}
          className="md:hidden text-white/70 hover:text-white p-1"
          aria-label="Close menu"
          data-testid="sidebar-close-btn"
        >
          <X size={20} />
        </button>
      </div>
      {user && (
        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
          {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full" /> : <UserCircle size={28} className="text-[#71717A]" />}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium truncate">{user.name}</p>
            <p className="text-[10px] text-[#71717A] truncate">{user.email}</p>
          </div>
          <button onClick={logout} className="text-[#71717A] hover:text-white" title="Logout" data-testid="logout-btn"><SignOut size={18} /></button>
        </div>
      )}
      <div className="px-4 py-4 border-b border-white/10">
        <label className="text-[10px] uppercase tracking-widest text-white/50 font-semibold mb-2 block">Active Month</label>
        <div className="relative">
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-full bg-[#1A1A1A] border border-white/20 text-white py-2 px-3 pr-8 text-sm font-medium appearance-none cursor-pointer hover:border-[#FFEB00] transition-colors"
            data-testid="global-month-selector">
            {monthOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <CalendarBlank size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#FFEB00] pointer-events-none" />
        </div>
      </div>
      <nav className="py-4">
        {links.map((link) => (
          <Link key={link.path} to={link.path} onClick={onClose} data-testid={`nav-${link.label.toLowerCase().replace(' ','-')}`}
            className={`sidebar-link ${location.pathname === link.path || (link.path !== "/" && location.pathname.startsWith(link.path)) ? "active" : ""}`}>
            <link.icon size={20} weight={location.pathname === link.path ? "fill" : "regular"} />
            {link.label}
          </Link>
        ))}
        {hasPermission("rios") && (
          <>
            <div className="px-4 mt-4 mb-2"><span className="text-[10px] uppercase tracking-widest text-white/30 font-semibold">Intelligence</span></div>
            <Link to="/rios" onClick={onClose} data-testid="nav-rios" className={`sidebar-link ${location.pathname.startsWith("/rios") ? "active" : ""}`}>
              <Brain size={20} weight={location.pathname.startsWith("/rios") ? "fill" : "regular"} />
              Relationship OS
            </Link>
          </>
        )}
        {user?.role === "super_admin" && (
          <Link to="/admin" onClick={onClose} data-testid="nav-admin" className={`sidebar-link ${location.pathname === "/admin" ? "active" : ""}`}>
            <Gear size={20} weight={location.pathname === "/admin" ? "fill" : "regular"} />
            Admin
          </Link>
        )}
      </nav>
      {/* Theme toggle pinned at bottom */}
      <div className="mt-auto border-t border-white/10">
        <ThemeToggle />
      </div>
    </aside>
  );
};

// ==================== MOBILE TOPBAR (hamburger) ====================
const MobileTopbar = ({ onOpen }) => {
  const { branding } = useBrand();
  return (
  <header className="mobile-topbar">
    <button
      type="button"
      onClick={onOpen}
      className="hamburger"
      aria-label="Open menu"
      data-testid="mobile-menu-btn"
    >
      <List size={22} />
    </button>
    <img src={branding.logo_base64 || "/digital-impressions-logo.png"} alt={branding.portal_title || "Digital Impressions"} />
    <div className="ml-auto">
      <ThemeToggle compact />
    </div>
  </header>
  );
};

// ==================== STATUS BADGES ====================
const StatusBadge = ({ status, deactivationDate }) => {
  const cls = { active: "badge-active", inactive: "badge-inactive", partial: "badge-revised", draft: "badge-draft", final: "badge-final",
    revised: "badge-revised", paid: "badge-active", sent: "badge-final", revised_adjustment_required: "badge-revised",
    unpaid: "badge-draft", partially_paid: "badge-revised", overdue: "badge-revised" };
  const labels = { revised_adjustment_required: "REVISION REQ", partially_paid: "PARTIAL", unpaid: "UNPAID", overdue: "OVERDUE",
    partial: deactivationDate ? `ACTIVE TILL ${deactivationDate}` : "PARTIAL" };
  return <span className={`badge ${cls[status] || "badge-draft"}`} data-testid={`status-${status}`}>{labels[status] || status}</span>;
};

const PaymentStatusBadge = ({ status }) => {
  const colors = { unpaid: "text-[#A1A1AA] bg-[#1A1A1A]", partially_paid: "text-[#FFEB00] bg-[#1A1A00]",
    paid: "text-[#00C853] bg-[#001A0D]", overdue: "text-[#FF2A2A] bg-[#1A0000]" };
  const labels = { unpaid: "Unpaid", partially_paid: "Partially Paid", paid: "Paid", overdue: "Overdue" };
  return <span className={`px-2 py-1 text-xs font-semibold uppercase ${colors[status] || colors.unpaid}`} data-testid={`payment-status-${status}`}>{labels[status] || "Unpaid"}</span>;
};

// ==================== DASHBOARD ====================
const Dashboard = () => {
  const { user } = useAuth();
  const isClientUser = user?.role === "client_user";
  const { selectedMonth, monthLabel } = useMonth();
  const [summary, setSummary] = useState(null);
  const [clientsRevenue, setClientsRevenue] = useState([]);
  const [collections, setCollections] = useState(null);
  const [projectedBilling, setProjectedBilling] = useState(null);
  const [expectedBilling, setExpectedBilling] = useState(null);
  const [fixedOnlyActual, setFixedOnlyActual] = useState(null);
  const [fixedOnlyExpected, setFixedOnlyExpected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBillingDetails, setShowBillingDetails] = useState(false);
  const [showFixedOnlyDetails, setShowFixedOnlyDetails] = useState(false);
  const [billingDetailsView, setBillingDetailsView] = useState("actual"); // "actual" or "expected"
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  const fetchSyncStatus = async () => {
    try {
      const res = await api.get(`/sync-status`);
      setSyncStatus(res.data);
    } catch { /* ignore */ }
  };

  const fetchData = async () => {
    setLoading(true);
    if (isClientUser) {
      // Client users see ONLY their assigned clients' revenue — no billing/projection data.
      try {
        const revenueRes = await api.get(`/dashboard/clients-revenue?month=${selectedMonth}`);
        setClientsRevenue(revenueRes.data.clients_with_revenue || []);
      } catch (err) { console.error("Dashboard fetch error:", err); }
      finally { setLoading(false); }
      return;
    }
    try {
      const [summaryRes, revenueRes, collRes, projRes] = await Promise.all([
        api.get(`/dashboard/summary?month=${selectedMonth}`),
        api.get(`/dashboard/clients-revenue?month=${selectedMonth}`),
        api.get(`/dashboard/collections?month=${selectedMonth}`),
        api.get(`/dashboard/projected-billing?month=${selectedMonth}`)
      ]);
      setSummary(summaryRes.data.summary);
      setClientsRevenue(revenueRes.data.clients_with_revenue || []);
      setCollections(collRes.data);
      setProjectedBilling(projRes.data.projected_billing);
      setExpectedBilling(projRes.data.expected_projected_billing);
      setFixedOnlyActual(projRes.data.fixed_only_billing);
      const exp = projRes.data.expected_projected_billing || {};
      setFixedOnlyExpected({
        total_projected_inr: exp.fixed_only_total_inr || 0,
        clients_calculated: exp.fixed_only_count || 0,
        client_details: exp.fixed_only_details || [],
      });
    } catch (err) { console.error("Dashboard fetch error:", err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [selectedMonth]);
  useEffect(() => {
    fetchSyncStatus();
    const id = setInterval(fetchSyncStatus, 60_000); // refresh every minute
    return () => clearInterval(id);
  }, []);

  const syncAllRevenue = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/dashboard/sync-all-revenue?month=${selectedMonth}`);
      alert(`Synced ${res.data.synced} clients! (${res.data.failed} failed)`);
      fetchData();
    } catch { alert("Failed to sync"); }
    finally { setSyncing(false); }
  };

  if (loading) return <div className="p-8 text-[#71717A]">Loading...</div>;

  // Client User dashboard — minimal scoped view: just their assigned clients & revenue.
  if (isClientUser) {
    const totalRevenue = (clientsRevenue || []).reduce((s, c) => s + (c.gross_sales || c.net_sales || 0), 0);
    return (
      <div data-testid="client-user-dashboard">
        <div className="mb-8 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold">Dashboard</h1>
            <p className="text-[#52525B] mt-1">{monthLabel} · {clientsRevenue.length} {clientsRevenue.length === 1 ? "client" : "clients"}</p>
          </div>
          <button
            onClick={syncAllRevenue}
            disabled={syncing}
            className="btn-primary flex items-center gap-2"
            data-testid="client-user-sync-btn"
          >
            <ArrowsClockwise size={16} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Syncing…" : "Sync Revenue"}
          </button>
        </div>
        {totalRevenue > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="card-flat p-5" data-testid="client-user-total">
              <div className="text-[11px] uppercase tracking-wider text-[#71717A]">Combined Revenue · {monthLabel}</div>
              <div className="text-3xl font-mono mt-1">₹{totalRevenue.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
              <div className="text-[11px] text-[#71717A] mt-1">
                {(clientsRevenue || []).reduce((s, c) => s + (c.order_count || 0), 0)} orders total
              </div>
            </div>
            <div className="card-flat p-5" data-testid="client-user-projected">
              <div className="text-[11px] uppercase tracking-wider text-[#71717A]">Projected Month-End</div>
              <div className="text-3xl font-mono mt-1">
                {(() => {
                  const proj = (clientsRevenue || []).reduce((s, c) => s + (c.expected_monthly_revenue || c.projected_monthly_revenue || c.gross_sales || 0), 0);
                  return `₹${proj.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
                })()}
              </div>
              <div className="text-[11px] text-[#71717A] mt-1">Based on month-to-date trajectory</div>
            </div>
          </div>
        )}

        {/* Daily revenue chart — scoped to assigned clients on the backend */}
        <DailyRevenueByClient />

        <div className="mt-6">
          <h2 className="text-base font-semibold mb-3 text-white">Clients</h2>
          {clientsRevenue.length === 0 ? (
            <div className="card-flat p-8 text-center text-[#71717A]">No data yet for {monthLabel}.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clientsRevenue.map((c) => (
                <Link
                  key={c.client_id}
                  to={`/clients/${c.client_id}`}
                  className="card-flat p-5 hover:border-[#FFEB00]/40 transition-colors"
                  data-testid={`client-card-${c.client_id}`}
                >
                  <div className="text-sm font-semibold text-white">{c.client_name || c.name}</div>
                  {c.project_name && <div className="text-[11px] text-[#71717A] mt-0.5">{c.project_name}</div>}
                  <div className="mt-3 text-2xl font-mono text-white">
                    ₹{Number(c.gross_sales || c.net_sales || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                  </div>
                  <div className="text-[11px] text-[#71717A] mt-1">{c.order_count || 0} orders</div>
                  {(c.expected_monthly_revenue || c.projected_monthly_revenue) && (
                    <div className="text-[11px] text-[#71717A] mt-2 pt-2 border-t border-[#2A2A2A]">
                      Projected month-end: <span className="text-white font-mono">₹{Number(c.expected_monthly_revenue || c.projected_monthly_revenue).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const metrics = collections?.metrics || {};

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-[#52525B] mt-1">{monthLabel} Overview</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {syncStatus?.last_run?.run_at && (
            <div
              className="text-[10px] flex items-center gap-1.5 px-2.5 py-1.5 rounded"
              style={{ background: "#10B98115", color: "#86EFAC" }}
              title={`Last cron run: ${new Date(syncStatus.last_run.run_at).toLocaleString()} · ${syncStatus.last_run.success || 0} synced · ${syncStatus.last_run.failed || 0} failed · ${syncStatus.interval}`}
              data-testid="last-sync-badge"
            >
              <ArrowsClockwise size={12} weight="bold" />
              <span>Last sync: {timeAgo(syncStatus.last_run.run_at)}</span>
              <span className="text-[#52525B] mx-1">·</span>
              <span className="text-[#71717A]">{syncStatus.last_run.success || 0} ok</span>
              {(syncStatus.last_run.failed || 0) > 0 && (
                <span className="text-[#FF9999]"> · {syncStatus.last_run.failed} failed</span>
              )}
            </div>
          )}
          <button onClick={syncAllRevenue} className="btn-secondary flex items-center gap-2" disabled={syncing} data-testid="sync-revenue-btn">
            <ArrowsClockwise size={18} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing..." : "Sync All Revenue"}
          </button>
          <Link to="/clients/new" className="btn-primary flex items-center gap-2" data-testid="add-client-btn"><Plus size={18} /> Add Client</Link>
        </div>
      </div>

      {/* Daily Revenue by Client — pinned to top */}
      <DailyRevenueByClient />

      {/* TOTAL BILLING — hero row (Performance + Fixed Fee combined) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="card-flat p-7 border-l-4 border-l-[#FFEB00]" data-testid="total-billing-actual-card">
          <span className="small-label">Total Billing (Actual) - {monthLabel}</span>
          <p className="metric-value mt-3" style={{fontSize:'2.75rem', lineHeight:1.05, color:'#FFEB00'}} data-testid="total-billing-actual-value">
            {formatCurrency((projectedBilling?.total_projected_inr || 0) + (fixedOnlyActual?.total_projected_inr || 0))}
          </p>
          <p className="text-sm text-[#71717A] mt-2">Performance + Fixed Fee · {(projectedBilling?.clients_calculated || 0) + (fixedOnlyActual?.clients_calculated || 0)} clients</p>
        </div>
        <div className="card-flat p-7 border-l-4 border-l-[#00C853]" data-testid="total-billing-expected-card">
          <span className="small-label">Total Billing (Expected) - {monthLabel}</span>
          <p className="metric-value mt-3" style={{fontSize:'2.75rem', lineHeight:1.05, color:'#00C853'}} data-testid="total-billing-expected-value">
            {formatCurrency((expectedBilling?.total_projected_inr || 0) + (fixedOnlyExpected?.total_projected_inr || 0))}
          </p>
          <p className="text-sm text-[#71717A] mt-2">Performance + Fixed Fee · {(expectedBilling?.clients_calculated || 0) + (fixedOnlyExpected?.clients_calculated || 0)} clients</p>
        </div>
      </div>

      {/* Revenue & Billing cards - 2x2 grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="card-flat p-6 border-l-4 border-l-[#FFEB00]">
          <span className="small-label">Actual Revenue (MTD) - {monthLabel}</span>
          <p className="metric-value text-3xl mt-2" data-testid="actual-revenue-value">{formatCurrency(summary?.total_actual_revenue || 0)}</p>
          <p className="text-sm text-[#71717A] mt-1">{summary?.clients_with_revenue || 0} clients synced</p>
        </div>
        <div className="card-flat p-6 border-l-4 border-l-[#00C853]">
          <span className="small-label">Expected Monthly Revenue - {monthLabel}</span>
          <p className="metric-value text-3xl mt-2" style={{color:'#00C853'}} data-testid="expected-revenue-value">{formatCurrency(summary?.total_expected_revenue || 0)}</p>
        </div>
        <div className="card-flat p-6 border-l-4 border-l-[#8B5CF6] cursor-pointer hover:bg-[#1A1A2A] transition-colors" onClick={() => { setShowBillingDetails(true); setBillingDetailsView("actual"); }} data-testid="projected-billing-card">
          <span className="small-label">Performance Billing (Actual) - {monthLabel}</span>
          <p className="metric-value text-3xl mt-2" style={{color:'#8B5CF6'}} data-testid="projected-billing-value">{formatCurrency(projectedBilling?.total_projected_inr || 0)}</p>
          <p className="text-sm text-[#71717A] mt-1">{projectedBilling?.clients_calculated || 0} performance clients | Click for details</p>
        </div>
        <div className="card-flat p-6 border-l-4 border-l-[#00BCD4] cursor-pointer hover:bg-[#0A1A1A] transition-colors" onClick={() => { setShowBillingDetails(true); setBillingDetailsView("expected"); }} data-testid="expected-billing-card">
          <span className="small-label">Performance Billing (Expected) - {monthLabel}</span>
          <p className="metric-value text-3xl mt-2" style={{color:'#00BCD4'}} data-testid="expected-billing-value">{formatCurrency(expectedBilling?.total_projected_inr || 0)}</p>
          <p className="text-sm text-[#71717A] mt-1">{expectedBilling?.clients_calculated || 0} performance clients | Click for details</p>
        </div>
      </div>

      {/* Fixed Fee (Non-Performance) Billing - separate segment */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div
          className="card-flat p-6 border-l-4 border-l-[#F59E0B] cursor-pointer hover:bg-[#1A1410] transition-colors"
          onClick={() => setShowFixedOnlyDetails(v => !v)}
          data-testid="fixed-only-actual-card"
        >
          <span className="small-label">Fixed Fee Billing (Actual) - {monthLabel}</span>
          <p className="metric-value text-3xl mt-2" style={{color:'#F59E0B'}} data-testid="fixed-only-actual-value">{formatCurrency(fixedOnlyActual?.total_projected_inr || 0)}</p>
          <p className="text-sm text-[#71717A] mt-1">{fixedOnlyActual?.clients_calculated || 0} non-performance clients | Click for details</p>
        </div>
        <div
          className="card-flat p-6 border-l-4 border-l-[#FB7185] cursor-pointer hover:bg-[#1A1014] transition-colors"
          onClick={() => setShowFixedOnlyDetails(v => !v)}
          data-testid="fixed-only-expected-card"
        >
          <span className="small-label">Fixed Fee Billing (Expected) - {monthLabel}</span>
          <p className="metric-value text-3xl mt-2" style={{color:'#FB7185'}} data-testid="fixed-only-expected-value">{formatCurrency(fixedOnlyExpected?.total_projected_inr || 0)}</p>
          <p className="text-sm text-[#71717A] mt-1">{fixedOnlyExpected?.clients_calculated || 0} non-performance clients | Click for details</p>
        </div>
      </div>

      {/* Fixed-only client details */}
      {showFixedOnlyDetails && (
        <div className="card-flat mb-6" data-testid="fixed-only-details">
          <div className="p-4 border-b border-[#2A2A2A] flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#F59E0B]">Fixed Fee Clients (Non-Performance)</h3>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-[#71717A]">Actual Total: <span className="data-number font-bold" style={{color:'#F59E0B'}}>{formatCurrency(fixedOnlyActual?.total_projected_inr)}</span></span>
              <span className="text-[#71717A]">Expected Total: <span className="data-number font-bold" style={{color:'#FB7185'}}>{formatCurrency(fixedOnlyExpected?.total_projected_inr)}</span></span>
            </div>
          </div>
          <table className="table-dense">
            <thead><tr><th>Client</th><th className="text-right">Fixed Fee</th><th className="text-right">Total Billable</th><th>Status</th></tr></thead>
            <tbody>
              {(fixedOnlyActual?.client_details || []).map(c => (
                <tr key={c.client_id}>
                  <td className="font-medium"><Link to={`/clients/${c.client_id}`} className="hover:text-[#F59E0B] transition-colors">{c.client_name}</Link></td>
                  <td className="text-right data-number">{formatCurrency(c.fixed_fee)}</td>
                  <td className="text-right data-number font-medium" style={{color:'#F59E0B'}}>{formatCurrency(c.total_billable)}</td>
                  <td className="text-xs capitalize">{c.effective_status || "active"}</td>
                </tr>
              ))}
              {(!fixedOnlyActual?.client_details || fixedOnlyActual.client_details.length === 0) && (
                <tr><td colSpan={4} className="text-center text-[#71717A] py-6">No fixed-fee (non-performance) clients for this month.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Projected Billing Details */}
      {showBillingDetails && (
        <div className="card-flat mb-6" data-testid="projected-billing-details">
          <div className="p-4 border-b border-[#2A2A2A] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button onClick={() => setBillingDetailsView("actual")}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${billingDetailsView === "actual" ? "bg-[#8B5CF6] text-white" : "bg-[#1A1A1A] text-[#71717A] hover:text-white"}`}
                data-testid="billing-tab-actual">Based on Actual</button>
              <button onClick={() => setBillingDetailsView("expected")}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${billingDetailsView === "expected" ? "bg-[#00BCD4] text-white" : "bg-[#1A1A1A] text-[#71717A] hover:text-white"}`}
                data-testid="billing-tab-expected">Based on Expected</button>
            </div>
            <div className="flex items-center gap-4 text-sm">
              {billingDetailsView === "actual" ? (<>
                <span className="text-[#71717A]">Fixed: <span className="data-number text-white">{formatCurrency(projectedBilling?.total_fixed_fee)}</span></span>
                <span className="text-[#71717A]">Variable: <span className="data-number text-white">{formatCurrency(projectedBilling?.total_variable_fee)}</span></span>
                <span className="text-[#71717A]">Total: <span className="data-number font-bold" style={{color:'#8B5CF6'}}>{formatCurrency(projectedBilling?.total_projected_inr)}</span></span>
              </>) : (<>
                <span className="text-[#71717A]">Fixed: <span className="data-number text-white">{formatCurrency(expectedBilling?.total_fixed_fee)}</span></span>
                <span className="text-[#71717A]">Variable: <span className="data-number text-white">{formatCurrency(expectedBilling?.total_variable_fee)}</span></span>
                <span className="text-[#71717A]">Total: <span className="data-number font-bold" style={{color:'#00BCD4'}}>{formatCurrency(expectedBilling?.total_projected_inr)}</span></span>
              </>)}
            </div>
          </div>
          <table className="table-dense">
            <thead><tr><th>Client</th><th className="text-right">{billingDetailsView === "actual" ? "Actual Revenue" : "Expected Revenue"}</th><th className="text-right">Fixed Fee</th><th className="text-right">Variable Fee</th><th className="text-right">Total Billable</th><th>Model</th></tr></thead>
            <tbody>
              {billingDetailsView === "actual" ? (
                projectedBilling?.client_details?.map(c => (
                  <tr key={c.client_id}>
                    <td className="font-medium"><Link to={`/clients/${c.client_id}`} className="hover:text-[#8B5CF6] transition-colors">{c.client_name}</Link></td>
                    <td className="text-right data-number">{formatCurrency(c.effective_revenue)}</td>
                    <td className="text-right data-number">{formatCurrency(c.fixed_fee)}</td>
                    <td className="text-right data-number">{formatCurrency(c.variable_fee)}</td>
                    <td className="text-right data-number font-medium" style={{color:'#8B5CF6'}}>{formatCurrency(c.total_billable)}</td>
                    <td className="text-xs capitalize">{c.billing_model?.replace("_"," ")}</td>
                  </tr>
                ))
              ) : (
                expectedBilling?.client_details?.map(c => (
                  <tr key={c.client_id}>
                    <td className="font-medium"><Link to={`/clients/${c.client_id}`} className="hover:text-[#00BCD4] transition-colors">{c.client_name}</Link></td>
                    <td className="text-right data-number">{formatCurrency(c.expected_revenue)}</td>
                    <td className="text-right data-number">{formatCurrency(c.fixed_fee)}</td>
                    <td className="text-right data-number">{formatCurrency(c.variable_fee)}</td>
                    <td className="text-right data-number font-medium" style={{color:'#00BCD4'}}>{formatCurrency(c.total_billable)}</td>
                    <td className="text-xs capitalize">{c.billing_model?.replace("_"," ")}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Collection metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card-flat p-5 border-l-4 border-l-[#3B82F6]" data-testid="metric-total-invoiced">
          <span className="small-label">Total Invoiced</span>
          <p className="data-number text-xl mt-1">{formatCurrency(metrics.total_invoiced)}</p>
        </div>
        <div className="card-flat p-5 border-l-4 border-l-[#00C853]" data-testid="metric-total-collected">
          <span className="small-label">Total Collected (MTD)</span>
          <p className="data-number text-xl mt-1" style={{color:'#00C853'}}>{formatCurrency(metrics.total_collected)}</p>
        </div>
        <div className="card-flat p-5 border-l-4 border-l-[#FFEB00]" data-testid="metric-total-outstanding">
          <span className="small-label">Total Outstanding</span>
          <p className="data-number text-xl mt-1" style={{color:'#FFEB00'}}>{formatCurrency(metrics.total_outstanding)}</p>
        </div>
        <div className="card-flat p-5 border-l-4 border-l-[#FF2A2A]" data-testid="metric-overdue">
          <span className="small-label">Overdue Amount</span>
          <p className="data-number text-xl mt-1" style={{color:'#FF2A2A'}}>{formatCurrency(metrics.overdue_amount)}</p>
        </div>
      </div>

      {/* Client-wise pending */}
      {collections?.client_pending?.length > 0 && (
        <div className="card-flat mb-6" data-testid="client-pending-table">
          <div className="p-4 border-b border-[#2A2A2A]"><h2 className="text-lg font-bold">Client-wise Pending</h2></div>
          <table className="table-dense">
            <thead><tr><th>Client</th><th className="text-right">Outstanding</th><th>Invoices</th></tr></thead>
            <tbody>
              {collections.client_pending.map(cp => (
                <tr key={cp.client_id}><td><Link to={`/clients/${cp.client_id}`} className="hover:text-[#FFEB00]">{cp.client_id}</Link></td>
                  <td className="text-right data-number" style={{color:'#FFEB00'}}>{formatCurrency(cp.outstanding)}</td>
                  <td>{cp.invoice_count}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Day-wise Revenue Pacing */}
      <div className="mb-6" data-testid="dashboard-daily-charts">
        <DailyRevenueCharts month={selectedMonth} monthLabel={monthLabel} />
      </div>

      {/* Revenue table */}
      <div className="card-flat mb-6" data-testid="client-revenue-table">
        <div className="p-4 border-b border-[#2A2A2A] flex items-center justify-between">
          <h2 className="text-lg font-bold">Client Revenue - {monthLabel}</h2>
          <span className="text-sm text-[#71717A]">{clientsRevenue.length + (fixedOnlyActual?.client_details?.length || 0)} clients ({clientsRevenue.length} performance · {fixedOnlyActual?.client_details?.length || 0} fixed fee)</span>
        </div>
        {clientsRevenue.length === 0 && !(fixedOnlyActual?.client_details?.length) ? (
          <div className="p-8 text-center text-[#71717A]">No revenue data for {monthLabel}.
            <button onClick={syncAllRevenue} className="btn-primary mt-4" disabled={syncing} data-testid="sync-revenue-empty-btn">{syncing ? "Syncing..." : "Sync Now"}</button>
          </div>
        ) : (
          <table className="table-dense">
            <thead>
              <tr><th>Client</th><th className="text-right">Gross</th><th className="text-right">Tax</th>
                <th className="text-right">Shipping</th><th className="text-right">Discounts</th><th className="text-right">Net</th>
                <th className="text-right">Expected</th><th>Orders</th></tr>
            </thead>
            <tbody>
              {clientsRevenue.map(rev => {
                const isForeign = rev.native_currency && rev.native_currency !== "INR";
                return (
                <tr key={rev.client_id} data-testid={`revenue-row-${rev.client_id}`}>
                  <td className="font-medium">
                    <Link to={`/clients/${rev.client_id}`} className="hover:text-[#FFEB00] transition-colors">{rev.client_name}</Link>
                    {isForeign && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{background:'#00BCD422', color:'#00BCD4'}} title={`Native: ${rev.native_currency} — showing INR @ ${rev.fx_rate_used}`}>{rev.native_currency}→INR</span>
                    )}
                  </td>
                  <td className="text-right data-number font-semibold">{formatCurrency((rev.gross_sales || 0) + (rev.tax || 0) + (rev.shipping || 0), "INR")}</td>
                  <td className="text-right data-number text-[#A1A1AA]">{formatCurrency(rev.tax || 0, "INR")}</td>
                  <td className="text-right data-number text-[#A1A1AA]">{formatCurrency(rev.shipping || 0, "INR")}</td>
                  <td className="text-right data-number" style={{color:'#FF4444'}}>{rev.discounts > 0 ? `-${formatCurrency(rev.discounts, "INR")}` : formatCurrency(0, "INR")}</td>
                  <td className="text-right data-number" style={{color:'#FFEB00'}}>
                    {formatCurrency(rev.net_sales, "INR")}
                    {isForeign && <div className="text-[10px] text-[#71717A] mt-0.5">{formatCurrency(rev.net_sales_native, rev.native_currency)}</div>}
                  </td>
                  <td className="text-right data-number" style={{color:'#00C853'}}>{formatCurrency(rev.expected_monthly_revenue, "INR")}</td>
                  <td className="data-number">{rev.order_count}</td>
                </tr>
              );})}
              {/* Fixed-fee (non-performance) clients — no Shopify revenue; show fixed fee billed */}
              {(fixedOnlyActual?.client_details || []).map(c => (
                <tr key={`fixed-${c.client_id}`} data-testid={`fixed-revenue-row-${c.client_id}`} className="bg-[#0F0D06]">
                  <td className="font-medium">
                    <Link to={`/clients/${c.client_id}`} className="hover:text-[#F59E0B] transition-colors">{c.client_name}</Link>
                    <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{background:'#F59E0B22', color:'#F59E0B'}}>Fixed Fee</span>
                  </td>
                  <td className="text-right data-number text-[#52525B]">—</td>
                  <td className="text-right data-number text-[#52525B]">—</td>
                  <td className="text-right data-number text-[#52525B]">—</td>
                  <td className="text-right data-number text-[#52525B]">—</td>
                  <td className="text-right data-number" style={{color:'#F59E0B'}}>{formatCurrency(c.total_billable, c.currency)}</td>
                  <td className="text-right data-number" style={{color:'#F59E0B'}}>{formatCurrency(c.total_billable, c.currency)}</td>
                  <td className="data-number text-[#52525B]">—</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ==================== CLIENTS LIST ====================
const ClientsList = () => {
  const { selectedMonth } = useMonth();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const isClientUser = user?.role === "client_user";
  useEffect(() => {
    api.get(`/clients?include_inactive=true&month=${selectedMonth}`).then(res => {
      const list = res.data.clients || [];
      setClients(list);
      setLoading(false);
      // Auto-redirect: single-client client_user → straight to their dashboard
      if (isClientUser && list.length === 1) {
        navigate(`/clients/${list[0].id}`, { replace: true });
      }
    }).catch(() => setLoading(false));
  }, [selectedMonth, isClientUser, navigate]);
  const filteredClients = clients.filter(c => {
    const eff = c.effective_status || c.status;
    if (filter === "all") return true;
    if (filter === "active") return eff === "active" || eff === "partial";
    return eff === "inactive";
  });
  return (
    <div data-testid="clients-page">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="text-3xl font-bold">{isClientUser ? "My Clients" : "Clients"}</h1><p className="text-[#52525B] mt-1">{clients.length} total</p></div>
        <div className="flex items-center gap-2">
          {!isClientUser && (
            <button
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/clients/export.csv`, { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `revflow_clients_${new Date().toISOString().slice(0, 10)}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch (e) {
                  alert(e.response?.data?.detail || e.message || "Export failed");
                }
              }}
              className="btn-secondary flex items-center gap-2 text-sm"
              data-testid="export-clients-csv-btn"
              title="Download the full client list as CSV"
            >
              <FileText size={16} /> Export CSV
            </button>
          )}
          {!isClientUser && <Link to="/clients/new" className="btn-primary flex items-center gap-2" data-testid="add-client-btn"><Plus size={18} /> Add Client</Link>}
        </div>
      </div>
      <div className="tabs">
        {["all","active","inactive"].map(f => <button key={f} onClick={() => setFilter(f)} className={`tab ${filter===f?"active":""}`} data-testid={`filter-${f}`}>{f.charAt(0).toUpperCase()+f.slice(1)}</button>)}
      </div>
      <div className="card-flat">
        {loading ? <div className="p-8 text-center text-[#A1A1AA]">Loading...</div> : filteredClients.length === 0 ? <div className="p-8 text-center text-[#A1A1AA]">No clients</div> : (
          <table className="table-dense">
            <thead><tr><th>Client Name</th><th>Project</th><th>Revenue Basis</th><th>Billing Model</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
            <tbody>
              {filteredClients.map(client => (
                <tr key={client.id} data-testid={`client-row-${client.id}`}>
                  <td className="font-medium">
                    <Link to={`/clients/${client.id}`} className="hover:text-[#FFEB00]">{client.client_name}</Link>
                    {client.deactivation_date && <span className="text-xs text-[#FF4444] ml-2">deactivates {client.deactivation_date}</span>}
                  </td>
                  <td>{client.project_name}</td>
                  <td className="text-sm text-[#A1A1AA]">{REVENUE_BASIS_LABELS[client.revenue_basis] || "Gross"}</td>
                  <td className="capitalize">{client.billing_model?.replace("_"," ")}</td>
                  <td><StatusBadge status={client.effective_status || client.status} deactivationDate={client.deactivation_date} /></td>
                  <td className="text-right"><Link to={`/clients/${client.id}`} className="btn-ghost text-sm" data-testid={`view-client-${client.id}`}>View <CaretRight size={14} className="inline" /></Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ==================== CLIENT FORM ====================
const ClientForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = location.pathname.includes("/edit");
  const clientId = isEdit ? location.pathname.split("/")[2] : null;
  const { user } = useAuth();
  const isReadOnlyForIntegrations = user?.role === "client_user";

  const SERVICE_CATALOG = [
    { name: "Design & Development", hsn: "998314" },
    { name: "Digital Marketing", hsn: "998319" },
    { name: "Domain Buy & Renewals", hsn: "998315" },
    { name: "Email Solutions", hsn: "998315" },
    { name: "Hosting Solutions", hsn: "998315" },
    { name: "Multimedia Production", hsn: "998319" },
    { name: "Website Development", hsn: "998314" },
    { name: "Website Maintenance", hsn: "998313" },
    { name: "Services", hsn: "998316" },
    { name: "SEO", hsn: "998313" },
  ];

  const [formData, setFormData] = useState({
    client_name: "", project_name: "", zoho_org_id: "", zoho_org_name: "",
    zoho_customer_id: "", zoho_customer_name: "",
    shopify_store: "",
    shopify_stores: [], store_fx_rates: {}, currency: "INR",
    billing_model: "fixed", revenue_source: "manual", revenue_basis: "gross",
    exclude_tax: false, exclude_shipping: false, target_roas: 0,
    linked_client_ids: [], is_primary_billing_client: false,
    proposal_terms: "", start_date: "",
    facebook_ad_account_id: "",
    combine_invoices: false,
    services: [],
    hourly_rate: 0,
    hourly_hours: 0,
    inr_conversion_rate: 1,
  });
  const [zohoOrgs, setZohoOrgs] = useState([]);
  const [zohoContacts, setZohoContacts] = useState([]);
  const [shopifyStores, setShopifyStores] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [fbAdAccounts, setFbAdAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [orgRes, shopifyRes, clientsRes, fbRes] = await Promise.all([api.get("/zoho/organizations"), api.get("/shopify/stores"), api.get("/clients"), api.get("/facebook/connection")]);
        setZohoOrgs(orgRes.data.organizations || []);
        setShopifyStores(shopifyRes.data.stores || []);
        setAllClients((clientsRes.data.clients || []).filter(c => c.id !== clientId));
        setFbAdAccounts(fbRes.data.ad_accounts || []);
        if (isEdit && clientId) {
          const clientRes = await api.get(`/clients/${clientId}`);
          const c = clientRes.data.client;
          setFormData({
            client_name: c.client_name || "", project_name: c.project_name || "",
            zoho_org_id: c.zoho_org_id || "", zoho_org_name: c.zoho_org_name || "",
            zoho_customer_id: c.zoho_customer_id || "", zoho_customer_name: c.zoho_customer_name || "",
            shopify_store: c.shopify_store || "",
            shopify_stores: (c.shopify_stores && c.shopify_stores.length > 0) ? c.shopify_stores : (c.shopify_store ? [c.shopify_store] : []),
            store_fx_rates: c.store_fx_rates || {},
            currency: c.currency || "INR",
            billing_model: c.billing_model || "fixed", revenue_source: c.revenue_source || "manual",
            revenue_basis: c.revenue_basis || "gross",
            exclude_tax: c.exclude_tax || false, exclude_shipping: c.exclude_shipping || false,
            target_roas: c.target_roas ?? 0,
            linked_client_ids: c.linked_client_ids || [], is_primary_billing_client: c.is_primary_billing_client || false,
            proposal_terms: c.proposal_terms || "", start_date: c.start_date || "",
            facebook_ad_account_id: c.facebook_ad_account_id || "",
            combine_invoices: c.combine_invoices || false,
            services: c.services || [],
            hourly_rate: c.hourly_rate || 0,
            hourly_hours: c.hourly_hours || 0,
            inr_conversion_rate: c.inr_conversion_rate || 1,
          });
          if (c.zoho_org_id) fetchContacts(c.zoho_org_id);
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [isEdit, clientId]);

  const fetchContacts = async (orgId) => {
    if (!orgId) { setZohoContacts([]); return; }
    setLoadingContacts(true);
    try { const res = await api.get(`/zoho/contacts?contact_type=customer&org_id=${orgId}`); setZohoContacts(res.data.contacts || []); }
    catch { setZohoContacts([]); }
    finally { setLoadingContacts(false); }
  };

  const handleOrgChange = (e) => {
    const orgId = e.target.value;
    const org = zohoOrgs.find(o => o.id === orgId);
    setFormData({ ...formData, zoho_org_id: orgId, zoho_org_name: org?.name || "", zoho_customer_id: "", zoho_customer_name: "" });
    fetchContacts(orgId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/clients/${clientId}`, formData);
        navigate(`/clients/${clientId}`);
      } else {
        const res = await api.post("/clients", formData);
        navigate(`/clients/${res.data.client.id}`);
      }
    } catch { alert("Failed to save client"); }
    finally { setSaving(false); }
  };

  const set = (key, val) => setFormData({ ...formData, [key]: val });
  if (loading) return <div className="p-8 text-[#A1A1AA]">Loading...</div>;

  return (
    <div data-testid="client-form-page">
      <div className="mb-8"><h1 className="text-3xl font-bold">{isEdit ? "Edit Client" : "Add New Client"}</h1></div>
      <form onSubmit={handleSubmit} className="card-flat p-6 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="form-group"><label className="form-label">Client Name *</label>
            <input type="text" className="input-default w-full" value={formData.client_name} onChange={e => set("client_name", e.target.value)} required data-testid="input-client-name" /></div>
          <div className="form-group"><label className="form-label">Project Name <span className="text-[#71717A] text-xs">(Optional — used on invoice)</span></label>
            <input type="text" className="input-default w-full" value={formData.project_name} onChange={e => set("project_name", e.target.value)} placeholder="Leave blank to use client name" data-testid="input-project-name" /></div>

          {/* Services Selection */}
          <div className="form-group md:col-span-2 p-4 bg-[#1A1A1A] border border-[#2A2A2A]" data-testid="services-section">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="form-label text-[#FFEB00]">Services</label>
                <p className="text-xs text-[#71717A] mt-1">Add one row per service instance. You can add the same service multiple times (e.g. two Digital Marketing entries for different projects).</p>
              </div>
              <button type="button"
                onClick={() => setFormData(prev => ({ ...prev, services: [...prev.services, { name: "", hsn: "", project_name: "", fixed_cost: 0 }] }))}
                className="btn-secondary text-xs flex items-center gap-1" data-testid="add-service-row-btn">
                <Plus size={14} /> Add Service
              </button>
            </div>
            {formData.services.length === 0 && (
              <p className="text-xs text-[#71717A] py-3">No services yet. Click <span className="text-[#FFEB00]">+ Add Service</span> to get started.</p>
            )}
            {formData.services.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-[#71717A] px-2">
                  <div className="col-span-3">Service (HSN)</div>
                  <div className="col-span-3">Project Name</div>
                  <div className="col-span-2">Fixed Cost / mo</div>
                  <div className="col-span-3">Invoice</div>
                  <div className="col-span-1"></div>
                </div>
                {formData.services.map((svc, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`service-row-${idx}`}>
                    <select
                      className="input-default col-span-3 text-sm py-1.5 px-2"
                      value={svc.name}
                      onChange={e => {
                        const chosen = SERVICE_CATALOG.find(s => s.name === e.target.value);
                        const updated = [...formData.services];
                        updated[idx] = { ...updated[idx], name: e.target.value, hsn: chosen?.hsn || "" };
                        set("services", updated);
                      }}
                      data-testid={`svc-select-${idx}`}
                    >
                      <option value="">Select service…</option>
                      {SERVICE_CATALOG.map(s => (
                        <option key={s.name} value={s.name}>{s.name} ({s.hsn})</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      className="input-default col-span-3 text-sm py-1.5 px-2"
                      placeholder="e.g. Email Marketing, US SEO"
                      value={svc.project_name || ""}
                      onChange={e => {
                        const updated = [...formData.services];
                        updated[idx] = { ...updated[idx], project_name: e.target.value };
                        set("services", updated);
                      }}
                      data-testid={`svc-project-${idx}`}
                    />
                    <div className="col-span-2 flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        className="input-default text-sm py-1.5 px-2 flex-1"
                        placeholder="0"
                        value={svc.fixed_cost || ""}
                        onChange={e => {
                          const updated = [...formData.services];
                          updated[idx] = { ...updated[idx], fixed_cost: parseFloat(e.target.value) || 0 };
                          set("services", updated);
                        }}
                        data-testid={`svc-cost-${idx}`}
                      />
                      <span className="text-xs text-[#71717A]">{formData.currency}</span>
                    </div>
                    <div className="col-span-3 flex items-center">
                      <select
                        className="input-default text-sm py-1.5 px-2 w-full"
                        value={svc.separate_invoice ? "separate" : "line_item"}
                        onChange={e => {
                          const updated = [...formData.services];
                          updated[idx] = { ...updated[idx], separate_invoice: e.target.value === "separate" };
                          set("services", updated);
                        }}
                        data-testid={`svc-invoice-mode-${idx}`}
                        title="Same invoice as other services vs. create a separate Zoho invoice for this service"
                      >
                        <option value="line_item">Same invoice (line item)</option>
                        <option value="separate">Separate invoice</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = formData.services.filter((_, i) => i !== idx);
                        set("services", updated);
                      }}
                      className="col-span-1 text-[#FF4444] hover:text-[#FF6666] flex justify-center"
                      data-testid={`svc-remove-${idx}`}
                      title="Remove"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
                <p className="text-xs text-[#71717A] mt-2">Total fixed services: <span className="data-number text-[#FFEB00]">{formatCurrency(formData.services.reduce((s, v) => s + (v.fixed_cost || 0), 0), formData.currency)}</span> · <span className="text-[#F59E0B]">{formData.services.filter(s => s.separate_invoice).length}</span> marked as separate invoice</p>
              </div>
            )}
          </div>
          <div className="form-group"><label className="form-label">Zoho Organization</label>
            <select className="input-default w-full" value={formData.zoho_org_id} onChange={handleOrgChange} data-testid="select-zoho-org">
              <option value="">Select Organization</option>
              {zohoOrgs.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
            </select></div>
          <div className="form-group"><label className="form-label">Zoho Customer {loadingContacts && <span className="text-[#FFEB00] text-xs ml-2">Loading...</span>}</label>
            <select className="input-default w-full" value={formData.zoho_customer_id} disabled={!formData.zoho_org_id || loadingContacts}
              onChange={e => { const c = zohoContacts.find(c => c.contact_id === e.target.value); set("zoho_customer_id", e.target.value); setFormData(f => ({...f, zoho_customer_id: e.target.value, zoho_customer_name: c?.contact_name || ""})); }}
              data-testid="select-zoho-customer">
              <option value="">{formData.zoho_org_id ? "Select Customer" : "Select Org first"}</option>
              {zohoContacts.map(c => <option key={c.contact_id} value={c.contact_id}>{c.contact_name}</option>)}
            </select></div>
          <div className="form-group"><label className="form-label">Currency</label>
            <select className="input-default w-full" value={formData.currency} onChange={e => set("currency", e.target.value)} data-testid="select-currency">
              {["INR","USD","AED","EUR","GBP"].map(c => <option key={c} value={c}>{c}</option>)}
            </select></div>

          {/* Unified Shopify Connection — Agency vs Individual (one mode at a time) */}
          <ShopifyConnection
            clientId={clientId}
            isEdit={isEdit}
            formData={formData}
            setFormData={setFormData}
            shopifyStores={shopifyStores}
            currency={formData.currency}
            readOnly={isReadOnlyForIntegrations}
          />

          {/* Per-client Facebook (Meta Ads) connection */}
          {isEdit && (
            <div className={isReadOnlyForIntegrations ? "relative" : ""}>
              {isReadOnlyForIntegrations && (
                <div className="absolute top-2 right-2 z-10 text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-white/[0.06] border border-white/10 text-white/60 pointer-events-none" data-testid="facebook-readonly-badge">
                  Read-only · Super-admin only
                </div>
              )}
              <div className={isReadOnlyForIntegrations ? "pointer-events-none opacity-60" : ""}>
                <ClientFacebookConnection clientId={clientId} />
              </div>
            </div>
          )}

          {/* Per-client Google Analytics 4 connection */}
          {isEdit && (
            <div className={isReadOnlyForIntegrations ? "relative" : ""}>
              {isReadOnlyForIntegrations && (
                <div className="absolute top-2 right-2 z-10 text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-white/[0.06] border border-white/10 text-white/60 pointer-events-none" data-testid="ga4-readonly-badge">
                  Read-only · Super-admin only
                </div>
              )}
              <div className={isReadOnlyForIntegrations ? "pointer-events-none opacity-60" : ""}>
                <ClientGoogleAnalytics clientId={clientId} />
              </div>
            </div>
          )}

          {/* Per-client Google Ads connection */}
          {isEdit && (
            <ClientGoogleAds clientId={clientId} isSuperAdmin={user?.role === "super_admin" || user?.role === "admin_user"} />
          )}

          {/* Per-client Google Search Console connection */}
          {isEdit && (
            <ClientSearchConsole clientId={clientId} isSuperAdmin={user?.role === "super_admin" || user?.role === "admin_user"} />
          )}

          {/* Phase 2 · Revenue & Spend Mapping (super_admin + admin_user, edit only) */}
          {isEdit && (user?.role === "super_admin" || user?.role === "admin_user") && (
            <SpendMapping
              clientId={clientId}
              clientCurrency={formData?.currency || "INR"}
              fiscalWeekStart={formData?.fiscal_week_start || "mon"}
            />
          )}

          {/* Per-client Branded Portal — super_admin or admin_user, only on edit */}
          {isEdit && (user?.role === "super_admin" || user?.role === "admin_user") && (
            <div className="md:col-span-2" data-testid="client-branding-section">
              <BrandingEditor scope="client" clientId={clientId} title="Branded Client Portal" />
            </div>
          )}

          {/* Revenue Basis */}
          <div className="form-group md:col-span-2 p-4 bg-[#1A1A1A] border border-[#2A2A2A]" data-testid="revenue-basis-section">
            <label className="form-label text-[#FFEB00] mb-3">Revenue Basis (controls all billing calculations)</label>
            <p className="text-xs text-[#71717A] mb-3">Determines which revenue figure is used for fee calculations. Gross includes tax & shipping; Net excludes them.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[
                ["gross", "Gross Revenue", "Subtotal + Tax + Shipping"],
                ["minus_tax", "Revenue - Tax", "Subtotal + Shipping (no tax)"],
                ["minus_tax_shipping", "Revenue - Tax & Shipping", "Subtotal only (no tax/shipping)"],
                ["principal", "Principal Only", "Subtotal - Discounts"],
              ].map(([val, label, hint]) => (
                <label key={val} className={`flex flex-col gap-1 p-3 border cursor-pointer transition-colors ${formData.revenue_basis === val ? "border-[#FFEB00] bg-[#1A1A00]" : "border-[#2A2A2A] hover:border-[#FFEB00]/50"}`}>
                  <div className="flex items-center gap-2">
                    <input type="radio" name="revenue_basis" value={val} checked={formData.revenue_basis === val} onChange={e => set("revenue_basis", e.target.value)} className="accent-[#FFEB00]" data-testid={`revenue-basis-${val}`} />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                  <span className="text-xs text-[#71717A] ml-6">{hint}</span>
                </label>
              ))}
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.exclude_tax} onChange={e => set("exclude_tax", e.target.checked)} className="w-4 h-4 accent-[#FFEB00]" data-testid="toggle-exclude-tax" />
                <span className="text-sm">Exclude Tax/GST</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.exclude_shipping} onChange={e => set("exclude_shipping", e.target.checked)} className="w-4 h-4 accent-[#FFEB00]" data-testid="toggle-exclude-shipping" />
                <span className="text-sm">Exclude Shipping</span>
              </label>
            </div>
          </div>

          <div className="form-group"><label className="form-label">Billing Model</label>
            <select className="input-default w-full" value={formData.billing_model} onChange={e => set("billing_model", e.target.value)} data-testid="select-billing-model">
              <option value="fixed">Fixed Fee Only</option><option value="variable">Variable Fee Only</option>
              <option value="both">Both (Fixed + Variable)</option><option value="whichever_higher">Whichever Higher</option>
              <option value="hourly">Hourly</option>
            </select>
            {formData.billing_model === "hourly" && (
              <div className="mt-3 p-3 bg-[#1A1A1A] border border-[#2A2A2A] grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label text-xs">Hourly Rate ({formData.currency})</label>
                  <input type="number" step="0.01" min="0" className="input-default w-full" value={formData.hourly_rate} onChange={e => set("hourly_rate", parseFloat(e.target.value) || 0)} data-testid="input-hourly-rate" />
                </div>
                <div>
                  <label className="form-label text-xs">Hours / Month</label>
                  <input type="number" step="0.5" min="0" className="input-default w-full" value={formData.hourly_hours} onChange={e => set("hourly_hours", parseFloat(e.target.value) || 0)} data-testid="input-hourly-hours" />
                </div>
                <p className="col-span-2 text-xs text-[#FFEB00]">Total: {formatCurrency(formData.hourly_rate * formData.hourly_hours, formData.currency)}/mo</p>
              </div>
            )}
            {(formData.billing_model === "both" || formData.billing_model === "whichever_higher") && (
              <label className="flex items-center gap-2 cursor-pointer mt-3 p-2 bg-[#1A1A1A] border border-[#2A2A2A]">
                <input type="checkbox" checked={formData.combine_invoices} onChange={e => set("combine_invoices", e.target.checked)} className="w-4 h-4 accent-[#FFEB00]" data-testid="toggle-combine-invoices" />
                <div>
                  <span className="text-sm font-medium">Combine Fixed + Variable in One Invoice</span>
                  <p className="text-xs text-[#71717A]">Fixed Fee (current month) + Variable Fee (previous month) as two line items</p>
                </div>
              </label>
            )}</div>

          {/* Foreign Currency - INR Conversion */}
          {formData.currency !== "INR" && (
            <div className="form-group">
              <label className="form-label">INR Conversion Rate</label>
              <input type="number" step="0.01" min="0" className="input-default w-full" value={formData.inr_conversion_rate} onChange={e => set("inr_conversion_rate", parseFloat(e.target.value) || 1)} data-testid="input-inr-conversion" />
              <p className="text-xs text-[#71717A] mt-1">1 {formData.currency} = {formData.inr_conversion_rate} INR (for internal reporting)</p>
            </div>
          )}
          <div className="form-group"><label className="form-label">Target ROAS</label>
            <input type="number" step="0.1" min="0" className="input-default w-full" value={formData.target_roas} onChange={e => set("target_roas", parseFloat(e.target.value) || 0)} data-testid="input-target-roas" />
            <p className="text-xs text-[#71717A] mt-1">Used for ROAS color thresholds on Ad Spend page</p></div>
          <div className="form-group"><label className="form-label">Revenue Source</label>
            <select className="input-default w-full" value={formData.revenue_source} onChange={e => set("revenue_source", e.target.value)} data-testid="select-revenue-source">
              <option value="manual">Manual</option><option value="shopify">Shopify</option><option value="hybrid">Hybrid</option>
            </select></div>
          <div className="form-group"><label className="form-label">Start Date</label>
            <input type="date" className="input-default w-full" value={formData.start_date} onChange={e => set("start_date", e.target.value)} data-testid="input-start-date" /></div>
          <div className="form-group"><label className="form-label">Proposal Terms</label>
            <textarea className="input-default w-full" rows={2} value={formData.proposal_terms} onChange={e => set("proposal_terms", e.target.value)} data-testid="input-proposal-terms" /></div>
        </div>

        {/* Facebook Ad Account */}
        {fbAdAccounts.length > 0 && (
          <div className="mt-6 p-4 bg-[#1A1A1A] border border-[#2A2A2A]" data-testid="facebook-ad-account-section">
            <label className="form-label text-[#8B5CF6] mb-3">Facebook Ad Account</label>
            <p className="text-xs text-[#71717A] mb-3">Link a Facebook ad account to auto-sync ad spends for ROAS calculation.</p>
            <select className="input-default w-full" value={formData.facebook_ad_account_id} onChange={e => set("facebook_ad_account_id", e.target.value)} data-testid="facebook-ad-account-select">
              <option value="">None</option>
              {fbAdAccounts.map(acc => (
                <option key={acc.id} value={acc.account_id}>{acc.name || acc.id} ({acc.account_id}) {acc.currency ? `- ${acc.currency}` : ""}</option>
              ))}
            </select>
            {formData.facebook_ad_account_id && <p className="text-xs text-[#00C853] mt-2">Linked: {formData.facebook_ad_account_id}</p>}
          </div>
        )}

        {/* Linked Clients Section */}
        <div className="mt-6 p-4 bg-[#1A1A1A] border border-[#2A2A2A]" data-testid="linked-clients-section">
          <label className="form-label text-[#FFEB00] mb-3">Linked Clients (Combined Revenue Billing)</label>
          <p className="text-xs text-[#71717A] mb-3">Link other clients to combine their revenue into a single billing entity. This client becomes the primary billing client.</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {formData.linked_client_ids.map(lid => {
              const linkedClient = allClients.find(c => c.id === lid);
              return (
                <span key={lid} className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[#FFEB00]/10 border border-[#FFEB00]/30 rounded text-[#FFEB00]">
                  {linkedClient?.client_name || lid}
                  <button type="button" onClick={() => set("linked_client_ids", formData.linked_client_ids.filter(x => x !== lid))} className="hover:text-white ml-1">&times;</button>
                </span>
              );
            })}
          </div>
          <select className="input-default" onChange={e => {
            if (e.target.value && !formData.linked_client_ids.includes(e.target.value)) {
              set("linked_client_ids", [...formData.linked_client_ids, e.target.value]);
              set("is_primary_billing_client", true);
            }
            e.target.value = "";
          }} data-testid="link-client-select">
            <option value="">Add linked client...</option>
            {allClients.filter(c => c.id !== clientId && !formData.linked_client_ids.includes(c.id)).map(c => (
              <option key={c.id} value={c.id}>{c.client_name}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-4 mt-8 pt-6 border-t border-[#2A2A2A]">
          <button type="submit" className="btn-primary" disabled={saving} data-testid="save-client-btn">{saving ? "Saving..." : isEdit ? "Update Client" : "Create Client"}</button>
          <Link to="/clients" className="btn-secondary">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

// ==================== CLIENT DETAIL ====================
const ClientDetail = () => {
  const location = useLocation();
  const { selectedMonth, monthLabel } = useMonth();
  const { user } = useAuth();
  const isClientUser = user?.role === "client_user";
  const isAdminUser = user?.role === "admin_user";
  const canSeeBilling = !isClientUser && !isAdminUser;
  const clientId = location.pathname.split("/")[2];
  const [client, setClient] = useState(null);
  const [rules, setRules] = useState({});
  const [monthData, setMonthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(isClientUser ? "performance" : "overview");
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactDate, setDeactDate] = useState("");
  const [deactReason, setDeactReason] = useState("");

  const fetchClient = async () => {
    setLoading(true);
    // Fetch client + snapshot INDEPENDENTLY — a failing snapshot must NOT
    // prevent the client data from rendering (was causing spurious "Client not found").
    try {
      const clientRes = await api.get(`/clients/${clientId}`);
      setClient(clientRes.data.client);
      setRules(clientRes.data.rules || {});
    } catch (err) {
      console.error("Failed to load client:", err);
      setClient(null);
    }
    try {
      const snapshotRes = await api.get(`/clients/${clientId}/monthly-snapshot/${selectedMonth}`);
      setMonthData(snapshotRes.data);
    } catch (err) {
      console.error("Failed to load monthly snapshot:", err);
      setMonthData(null);
    }
    setLoading(false);
  };

  useEffect(() => { fetchClient(); }, [clientId, selectedMonth]);

  const handleDeactivate = async () => {
    if (!deactDate) { alert("Please select a deactivation date"); return; }
    if (!deactReason.trim()) { alert("Please provide a reason"); return; }
    try {
      await api.post(`/clients/${clientId}/deactivate`, { effective_date: deactDate, reason: deactReason });
      setShowDeactivateModal(false);
      setDeactDate(""); setDeactReason("");
      fetchClient();
    } catch (err) { alert("Failed: " + (err.response?.data?.detail || err.message)); }
  };

  const handleReactivate = async () => {
    if (!window.confirm(`Reactivate ${client?.client_name}? This will remove the deactivation date.`)) return;
    try {
      await api.post(`/clients/${clientId}/reactivate`);
      fetchClient();
    } catch (err) { alert("Failed: " + (err.response?.data?.detail || err.message)); }
  };

  if (loading) return <div className="p-8 text-[#A1A1AA]">Loading...</div>;
  if (!client) return <div className="p-8 text-[#A1A1AA]">Client not found</div>;
  const commSummary = monthData?.commission_summary || {};
  const billing = monthData?.billing?.saved_calculation || monthData?.billing?.auto_summary || {};
  const revenue = monthData?.revenue || {};

  // Compute effective status for selected month
  const getEffStatus = () => {
    if (!client.deactivation_date) return { status: "active" };
    const [y,m] = selectedMonth.split("-").map(Number);
    const daysInMonth = new Date(y, m, 0).getDate();
    const mStart = `${selectedMonth}-01`;
    const mEnd = `${selectedMonth}-${String(daysInMonth).padStart(2,"0")}`;
    if (client.deactivation_date <= mStart) return { status: "inactive" };
    if (client.deactivation_date > mEnd) return { status: "active" };
    const day = parseInt(client.deactivation_date.split("-")[2]);
    return { status: "partial", activeDays: day - 1, totalDays: daysInMonth };
  };
  const effStatus = getEffStatus();

  return (
    <div data-testid="client-detail-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">{client.client_name}</h1>
            {!isClientUser && <StatusBadge status={effStatus.status} deactivationDate={client.deactivation_date} />}
          </div>
          <p className="text-[#52525B] mt-1">{client.project_name}{!isClientUser && <> | {REVENUE_BASIS_LABELS[client.revenue_basis] || "Gross"}</>}{!isClientUser && client.zoho_org_name && <> | {client.zoho_org_name}</>}</p>
        </div>
        <div className="flex items-center gap-3">
          {!isClientUser && !client.deactivation_date && (
            <button onClick={() => { setDeactDate(""); setDeactReason(""); setShowDeactivateModal(true); }}
              className="btn-secondary text-[#FF4444] border-[#FF4444]/30 hover:bg-[#FF4444]/10" data-testid="deactivate-btn">
              Deactivate
            </button>
          )}
          {!isClientUser && client.deactivation_date && (
            <button onClick={handleReactivate}
              className="btn-secondary text-[#00C853] border-[#00C853]/30 hover:bg-[#00C853]/10" data-testid="reactivate-btn">
              Reactivate
            </button>
          )}
          {!isClientUser && (
            <Link to={`/clients/${clientId}/edit`} className="btn-secondary flex items-center gap-2" data-testid="edit-client-btn"><PencilSimple size={18} /> Edit</Link>
          )}
        </div>
      </div>

      {/* Deactivation info banner */}
      {client.deactivation_date && (
        <div className="mb-4 p-3 bg-[#1A0000] border border-[#FF4444]/30 text-sm flex items-center justify-between" data-testid="deactivation-banner">
          <span className="text-[#FF4444]">
            Deactivated from <strong>{client.deactivation_date}</strong>
            {client.deactivation_reason && <> — {client.deactivation_reason}</>}
            {effStatus.status === "partial" && <span className="text-[#FFEB00] ml-2">(Active {effStatus.activeDays}/{effStatus.totalDays} days this month)</span>}
          </span>
        </div>
      )}

      {/* Deactivation Modal */}
      {showDeactivateModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" data-testid="deactivate-modal">
          <div className="bg-[#111] border border-[#2A2A2A] rounded-lg p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-bold text-[#FF4444]">Deactivate {client.client_name}</h3>
            <p className="text-sm text-[#A1A1AA]">All data before the deactivation date will be preserved. Revenue and billing will only be calculated up to this date.</p>
            <div>
              <label className="form-label">Deactivate from which date? *</label>
              <input type="date" className="input-default w-full" value={deactDate} onChange={e => setDeactDate(e.target.value)} data-testid="deact-date-input" />
            </div>
            <div>
              <label className="form-label">Reason for deactivation *</label>
              <textarea className="input-default w-full" rows="3" value={deactReason} onChange={e => setDeactReason(e.target.value)}
                placeholder="e.g., Contract ended, Budget pause, Client request..." data-testid="deact-reason-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={handleDeactivate} className="btn-primary bg-[#FF4444] hover:bg-[#FF6666] flex-1" data-testid="confirm-deactivate-btn">Confirm Deactivation</button>
              <button onClick={() => setShowDeactivateModal(false)} className="btn-secondary flex-1" data-testid="cancel-deactivate-btn">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="tabs">
        {(isClientUser ? ["performance"] : canSeeBilling ? ["overview","monthly","rules","billing","invoices","performance"] : ["overview","monthly","rules","performance"]).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} className={`tab ${activeTab===tab?"active":""}`} data-testid={`tab-${tab}`}>
            {tab === "monthly" ? "Monthly Trends" : tab === "performance" ? "Performance" : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="space-y-6">
          <div className="card-flat p-6 border-l-4 border-l-[#FFEB00]" data-testid="commission-summary-card">
            <h3 className="text-lg font-semibold mb-4">Commission Summary - {monthLabel}</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div><span className="small-label">Revenue</span><p className="data-number mt-1 text-lg">{formatCurrency(commSummary.total_revenue, client.currency)}</p>
                <p className="text-xs text-[#71717A]">{revenue.revenue_source === "manual_override" ? "Manual" : revenue.revenue_source === "shopify" || revenue.revenue_source === "adjusted" ? "Shopify" : "No data"}</p></div>
              <div><span className="small-label">Fixed Fee</span><p className="data-number mt-1 text-lg">{formatCurrency(commSummary.fixed_fee, client.currency)}</p></div>
              <div><span className="small-label">Variable Fee</span><p className="data-number mt-1 text-lg">{formatCurrency(commSummary.variable_fee, client.currency)}</p></div>
              <div><span className="small-label">Total Billable</span><p className="data-number mt-1 text-lg font-bold" style={{color:'#FFEB00'}}>{formatCurrency(commSummary.total_billable, client.currency)}</p></div>
              <div><span className="small-label">Commission</span><p className="data-number mt-1 text-lg" style={{color:'#00C853'}}>{formatCurrency(commSummary.total_commission, client.currency)}</p></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card-flat p-6">
              <h3 className="text-lg font-semibold mb-4">Client Details</h3>
              <div className="space-y-3">
                <div><span className="small-label">Revenue Basis</span><p className="mt-1 text-[#FFEB00]">{REVENUE_BASIS_LABELS[client.revenue_basis] || "Gross"}</p></div>
                <div><span className="small-label">Deductions</span><p className="mt-1">{client.exclude_tax ? "Tax " : ""}{client.exclude_shipping ? "Shipping " : ""}{!client.exclude_tax && !client.exclude_shipping ? "None" : ""}</p></div>
                <div><span className="small-label">Billing Model</span><p className="capitalize mt-1">{client.billing_model?.replace("_"," ")}</p></div>
                <div><span className="small-label">Currency</span><p className="mt-1">{client.currency}</p></div>
                {client.services?.length > 0 && (
                  <div><span className="small-label">Services</span>
                    <div className="mt-1 space-y-1">
                      {client.services.map((svc, i) => (
                        <div key={i} className="flex items-center justify-between text-sm gap-2">
                          <span className="truncate">
                            {svc.name} <span className="text-[#71717A] text-xs">({svc.hsn})</span>
                            {svc.project_name && <span className="text-[#FFEB00] text-xs ml-2">· {svc.project_name}</span>}
                          </span>
                          {svc.fixed_cost > 0 && <span className="data-number text-[#FFEB00] shrink-0">{formatCurrency(svc.fixed_cost, client.currency)}/mo</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="card-flat p-6">
              <h3 className="text-lg font-semibold mb-4">Integrations</h3>
              <div className="space-y-3">
                <div><span className="small-label">Zoho Org</span><p className="mt-1">{client.zoho_org_name || "Not set"}</p></div>
                <div><span className="small-label">Zoho Customer</span><p className="mt-1">{client.zoho_customer_name || "Not linked"}</p></div>
                <div><span className="small-label">Shopify Store(s)</span>
                  {(client.shopify_stores?.length > 0 ? client.shopify_stores : (client.shopify_store ? [client.shopify_store] : [])).map(s => (
                    <p key={s} className="mt-1 text-sm">{s}</p>
                  ))}
                  {!client.shopify_store && !(client.shopify_stores?.length > 0) && <p className="mt-1 text-[#71717A]">Not linked</p>}
                </div>
              </div>
            </div>
          </div>
          {/* Day-wise Revenue Pacing for this client */}
          <DailyRevenueCharts clientId={clientId} month={selectedMonth} monthLabel={monthLabel} />
        </div>
      )}
      {activeTab === "monthly" && <ClientMonthlyView clientId={clientId} clientName={client.client_name} currency={client.currency} />}
      {activeTab === "rules" && (
        <div className="space-y-6">
          <div className="card-flat p-6"><h3 className="text-lg font-semibold mb-3">Fixed Fee</h3>
            {rules.fixed ? <div className="grid grid-cols-2 gap-4">
              <div><span className="small-label">Type</span><p className="capitalize mt-1">{rules.fixed.fixed_fee_type}</p></div>
              <div><span className="small-label">Amount</span><p className="data-number mt-1">{formatCurrency(rules.fixed.fixed_fee_amount, client.currency)}</p></div>
            </div> : <p className="text-[#A1A1AA]">No fixed fee rules</p>}
          </div>
          <div className="card-flat p-6"><h3 className="text-lg font-semibold mb-3">Variable Fee</h3>
            {rules.variable?.enabled ? <div className="grid grid-cols-2 gap-4">
              <div><span className="small-label">Trigger</span><p className="capitalize mt-1">{rules.variable.trigger_metric}</p></div>
              <div><span className="small-label">Type</span><p className="capitalize mt-1">{rules.variable.calculation_type}</p></div>
            </div> : <p className="text-[#A1A1AA]">Variable fee not enabled</p>}
          </div>
          <Link to={`/clients/${clientId}/rules`} className="btn-primary inline-flex items-center gap-2" data-testid="configure-rules-btn"><Gear size={18} /> Configure Rules</Link>
        </div>
      )}
      {activeTab === "billing" && (
        <div className="space-y-6">
          <BillingConfigPanel clientId={clientId} month={selectedMonth} monthLabel={monthLabel} currency={client.currency} onRefresh={fetchClient} clientData={client} />
          <div className="card-flat" data-testid="billing-history-table">
            <div className="p-4 border-b border-[#2A2A2A]"><h3 className="font-semibold">Billing History</h3></div>
            <BillingHistoryTable clientId={clientId} currency={client.currency} />
          </div>
        </div>
      )}
      {activeTab === "invoices" && <ClientInvoicesTab clientId={clientId} month={selectedMonth} monthLabel={monthLabel} currency={client.currency} />}
      {activeTab === "performance" && (
        <PerformanceIntelligence
          clientId={clientId}
          clientName={client.client_name}
          currency={client.currency}
          month={selectedMonth}
          monthLabel={monthLabel}
        />
      )}
    </div>
  );
};

const BillingHistoryTable = ({ clientId, currency }) => {
  const [calcs, setCalcs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get(`/billing/${clientId}`).then(r => { setCalcs(r.data.calculations || []); setLoading(false); }).catch(() => setLoading(false)); }, [clientId]);
  if (loading) return <div className="p-4 text-[#A1A1AA]">Loading...</div>;
  if (calcs.length === 0) return <div className="p-8 text-center text-[#A1A1AA]">No billing history</div>;
  return (
    <table className="table-dense"><thead><tr><th>Month</th><th className="text-right">Revenue</th><th className="text-right">Fixed</th><th className="text-right">Variable</th><th className="text-right">Total</th><th>Status</th></tr></thead>
      <tbody>{calcs.map(c => <tr key={c.id||c.month}><td className="font-medium">{c.month}</td><td className="text-right data-number">{formatCurrency(c.revenue,currency)}</td>
        <td className="text-right data-number">{formatCurrency(c.fixed_fee_billable,currency)}</td><td className="text-right data-number">{formatCurrency(c.variable_fee_billable,currency)}</td>
        <td className="text-right data-number font-medium">{formatCurrency(c.total_billable_fc,currency)}</td><td><StatusBadge status={c.status} /></td></tr>)}</tbody>
    </table>
  );
};

// ==================== CLIENT INVOICES TAB (with Payment Tracking) ====================
const ClientInvoicesTab = ({ clientId, month, monthLabel, currency }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentForm, setPaymentForm] = useState(null);
  const [payments, setPayments] = useState({});
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingOne, setSyncingOne] = useState(null);

  const fetchInvoices = async () => {
    try {
      const res = await api.get(`/invoices?client_id=${clientId}`);
      const all = res.data.invoices || [];
      setInvoices(all.filter(inv => inv.month === month));
      setLoading(false);
    } catch { setLoading(false); }
  };

  useEffect(() => { fetchInvoices(); }, [clientId, month]);

  const loadPayments = async (invoiceId) => {
    const res = await api.get(`/payments/invoice/${invoiceId}`);
    setPayments(prev => ({ ...prev, [invoiceId]: res.data }));
  };

  const recordPayment = async (invoiceId, data) => {
    try {
      await api.post("/payments/record", { invoice_id: invoiceId, ...data });
      setPaymentForm(null);
      await loadPayments(invoiceId);
      fetchInvoices();
    } catch (err) { alert("Failed: " + (err.response?.data?.detail || err.message)); }
  };

  const syncAllFromZoho = async () => {
    setSyncingAll(true);
    try {
      const res = await api.post(`/payments/sync-all-from-zoho?month=${month}`);
      const r = res.data.results;
      alert(`Zoho payment sync: ${r.synced} updated, ${r.skipped} skipped, ${r.failed} failed`);
      fetchInvoices();
    } catch (err) { alert("Sync failed: " + (err.response?.data?.detail || err.message)); }
    finally { setSyncingAll(false); }
  };

  const syncOneFromZoho = async (invoiceId) => {
    setSyncingOne(invoiceId);
    try {
      const res = await api.post(`/payments/sync-from-zoho/${invoiceId}`);
      if (res.data.success) {
        await loadPayments(invoiceId);
        fetchInvoices();
      } else { alert("Sync issue: " + JSON.stringify(res.data.result)); }
    } catch (err) { alert("Sync failed: " + (err.response?.data?.detail || err.message)); }
    finally { setSyncingOne(null); }
  };

  if (loading) return <div className="p-4 text-[#A1A1AA]">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="invoices-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Invoices - {monthLabel}</h3>
        {invoices.some(i => i.zoho_invoice_id) && (
          <button onClick={syncAllFromZoho} disabled={syncingAll} className="btn-secondary text-sm flex items-center gap-2" data-testid="sync-payments-zoho-btn">
            <ArrowsClockwise size={16} className={syncingAll ? "animate-spin" : ""} />
            {syncingAll ? "Syncing..." : "Sync Payments from Zoho"}
          </button>
        )}
      </div>

      {invoices.length === 0 ? <div className="card-flat p-8 text-center text-[#A1A1AA]">No invoices for {monthLabel}</div> : invoices.map(inv => (
        <div key={inv.id} className="card-flat" data-testid={`invoice-card-${inv.id}`}>
          <div className="p-4 flex items-center justify-between border-b border-[#2A2A2A]">
            <div className="flex items-center gap-4">
              <span className="font-semibold">{inv.zoho_invoice_number || inv.id?.slice(0,8)}</span>
              <span className="capitalize text-sm text-[#A1A1AA]">{inv.invoice_type}</span>
              <StatusBadge status={inv.status} />
              <PaymentStatusBadge status={inv.payment_status || "unpaid"} />
              {inv.zoho_status && <span className="text-xs text-[#71717A] capitalize">Zoho: {inv.zoho_status}</span>}
            </div>
            <div className="flex items-center gap-3">
              {inv.balance_due > 0 && <span className="text-sm text-[#FFEB00]">Due: {formatCurrency(inv.balance_due, currency)}</span>}
              <span className="data-number text-lg font-semibold">{formatCurrency(inv.amount, currency)}</span>
              {inv.zoho_invoice_id && (
                <button onClick={() => syncOneFromZoho(inv.id)} disabled={syncingOne === inv.id} className="btn-ghost text-xs flex items-center gap-1" data-testid={`sync-zoho-${inv.id}`}>
                  <ArrowsClockwise size={14} className={syncingOne === inv.id ? "animate-spin" : ""} /> Zoho
                </button>
              )}
              <button onClick={() => { loadPayments(inv.id); setPaymentForm(paymentForm === inv.id ? null : inv.id); }}
                className="btn-secondary text-sm flex items-center gap-1" data-testid={`record-payment-btn-${inv.id}`}>
                <Money size={16} /> {paymentForm === inv.id ? "Close" : "Payments"}
              </button>
            </div>
          </div>

          {paymentForm === inv.id && (
            <div className="p-4 bg-[#0A0A0A]" data-testid={`payment-section-${inv.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">Payment History</h4>
                  {payments[inv.id]?.payments?.length > 0 ? (
                    <div className="space-y-2">
                      {payments[inv.id].payments.map(p => (
                        <div key={p.id} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] text-sm flex items-center justify-between">
                          <div>
                            <span className="data-number font-semibold">{formatCurrency(p.amount, currency)}</span>
                            <span className="text-[#A1A1AA] ml-2">{p.payment_date}</span>
                            <span className="text-[#A1A1AA] ml-2 capitalize">{p.payment_mode}</span>
                            {p.reference && <span className="text-[#71717A] ml-2">Ref: {p.reference}</span>}
                            {p.source === "zoho" && <span className="text-[#3B82F6] ml-2 text-xs">[Zoho]</span>}
                          </div>
                          {p.source !== "zoho" && (
                            <button onClick={async () => { await api.delete(`/payments/${p.id}`); loadPayments(inv.id); fetchInvoices(); }}
                              className="text-[#FF2A2A] hover:underline text-xs" data-testid={`delete-payment-${p.id}`}>Remove</button>
                          )}
                        </div>
                      ))}
                      <div className="p-3 bg-[#1A1A00] border border-[#FFEB00]/30 text-sm">
                        <div className="flex justify-between"><span>Invoice Amount:</span><span className="data-number">{formatCurrency(payments[inv.id].summary.invoice_amount, currency)}</span></div>
                        <div className="flex justify-between"><span>Total Paid:</span><span className="data-number" style={{color:'#00C853'}}>{formatCurrency(payments[inv.id].summary.total_paid, currency)}</span></div>
                        <div className="flex justify-between font-semibold"><span>Balance Due:</span><span className="data-number" style={{color:'#FFEB00'}}>{formatCurrency(payments[inv.id].summary.balance_due, currency)}</span></div>
                      </div>
                    </div>
                  ) : <p className="text-[#A1A1AA] text-sm">No payments recorded. {inv.zoho_invoice_id ? 'Click "Zoho" to sync.' : ''}</p>}
                </div>
                <div>
                  <h4 className="font-semibold mb-3">Record Manual Payment</h4>
                  <RecordPaymentForm invoiceId={inv.id} currency={currency} invoiceAmount={inv.amount} balanceDue={payments[inv.id]?.summary?.balance_due ?? inv.amount} onSubmit={recordPayment} />
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const RecordPaymentForm = ({ invoiceId, currency, invoiceAmount, balanceDue, onSubmit }) => {
  const [amount, setAmount] = useState(balanceDue || 0);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0]);
  const [paymentMode, setPaymentMode] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (amount <= 0) { alert("Amount must be > 0"); return; }
    setSubmitting(true);
    await onSubmit(invoiceId, { amount, payment_date: paymentDate, payment_mode: paymentMode, reference, internal_note: note });
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="record-payment-form">
      <div className="form-group"><label className="form-label text-xs">Amount *</label>
        <input type="number" step="0.01" className="input-default w-full" value={amount} onChange={e => setAmount(parseFloat(e.target.value) || 0)} required data-testid="payment-amount" /></div>
      <div className="form-group"><label className="form-label text-xs">Payment Date *</label>
        <input type="date" className="input-default w-full" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} required data-testid="payment-date" /></div>
      <div className="form-group"><label className="form-label text-xs">Payment Mode *</label>
        <select className="input-default w-full" value={paymentMode} onChange={e => setPaymentMode(e.target.value)} data-testid="payment-mode">
          <option value="bank_transfer">Bank Transfer</option><option value="upi">UPI</option><option value="cheque">Cheque</option>
          <option value="cash">Cash</option><option value="other">Other</option>
        </select></div>
      <div className="form-group"><label className="form-label text-xs">Reference / UTR</label>
        <input type="text" className="input-default w-full" value={reference} onChange={e => setReference(e.target.value)} placeholder="UTR / Cheque No" data-testid="payment-reference" /></div>
      <div className="form-group"><label className="form-label text-xs">Internal Note</label>
        <input type="text" className="input-default w-full" value={note} onChange={e => setNote(e.target.value)} placeholder="Optional" data-testid="payment-note" /></div>
      <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={submitting} data-testid="submit-payment-btn">
        <CreditCard size={18} /> {submitting ? "Recording..." : "Record Payment"}
      </button>
    </form>
  );
};

// ==================== BILLING PAGE ====================
const BillingPage = () => {
  const { selectedMonth, monthLabel } = useMonth();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [calculations, setCalculations] = useState([]);
  useEffect(() => { api.get("/clients?status=active").then(r => setClients(r.data.clients || [])); }, []);
  useEffect(() => { if (selectedClient) api.get(`/billing/${selectedClient}`).then(r => setCalculations(r.data.calculations || [])); }, [selectedClient]);
  const monthCalc = calculations.find(c => c.month === selectedMonth);

  return (
    <div data-testid="billing-page">
      <div className="mb-8"><h1 className="text-3xl font-bold">Monthly Billing</h1><p className="text-[#52525B] mt-1">{monthLabel}</p></div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card-flat">
          <div className="p-4 border-b border-[#2A2A2A]"><span className="small-label">Select Client</span></div>
          <div className="max-h-96 overflow-y-auto">
            {clients.map(c => (
              <button key={c.id} onClick={() => setSelectedClient(c.id)} data-testid={`billing-client-${c.id}`}
                className={`w-full text-left p-4 border-b border-[#2A2A2A] hover:bg-[#1A1A1A] transition-colors ${selectedClient===c.id?"bg-[#1A1A1A] border-l-2 border-l-[#FFEB00]":""}`}>
                <p className="font-medium text-sm">{c.client_name}</p><p className="text-xs text-[#A1A1AA]">{c.project_name}</p>
              </button>
            ))}
          </div>
        </div>
        <div className="md:col-span-3 space-y-4">
          {selectedClient ? (
            <>
              <div className="card-flat p-4 border-l-4 border-l-[#FFEB00]" data-testid="current-month-billing">
                <div className="flex items-center justify-between mb-2"><h3 className="font-semibold">{monthLabel}</h3>
                  <Link to={`/billing/${selectedClient}/calculate`} className="btn-primary text-sm" data-testid="calculate-billing-btn">{monthCalc?"Recalculate":"Calculate"}</Link></div>
                {monthCalc ? (
                  <div className="grid grid-cols-4 gap-4 mt-3">
                    <div><span className="small-label">Revenue</span><p className="data-number mt-1">{formatCurrency(monthCalc.revenue)}</p></div>
                    <div><span className="small-label">Fixed</span><p className="data-number mt-1">{formatCurrency(monthCalc.fixed_fee_billable)}</p></div>
                    <div><span className="small-label">Variable</span><p className="data-number mt-1">{formatCurrency(monthCalc.variable_fee_billable)}</p></div>
                    <div><span className="small-label">Total</span><p className="data-number mt-1 font-bold" style={{color:'#FFEB00'}}>{formatCurrency(monthCalc.total_billable_fc)}</p></div>
                  </div>
                ) : <p className="text-[#A1A1AA] text-sm">No calculation yet.</p>}
              </div>
              <div className="card-flat" data-testid="billing-history"><div className="p-4 border-b border-[#2A2A2A]"><h3 className="font-semibold">All Months</h3></div>
                {calculations.length === 0 ? <div className="p-8 text-center text-[#A1A1AA]">No calculations</div> : (
                  <table className="table-dense"><thead><tr><th>Month</th><th className="text-right">Revenue</th><th className="text-right">Fixed</th><th className="text-right">Variable</th><th className="text-right">Total</th><th>Status</th></tr></thead>
                    <tbody>{calculations.map(c => <tr key={c.id||c.month} className={c.month===selectedMonth?"bg-[#1A1A00]":""}><td className="font-medium">{c.month} {c.month===selectedMonth&&<span className="text-[#FFEB00] text-xs ml-1">ACTIVE</span>}</td>
                      <td className="text-right data-number">{formatCurrency(c.revenue)}</td><td className="text-right data-number">{formatCurrency(c.fixed_fee_billable)}</td>
                      <td className="text-right data-number">{formatCurrency(c.variable_fee_billable)}</td><td className="text-right data-number font-medium">{formatCurrency(c.total_billable_fc)}</td><td><StatusBadge status={c.status} /></td></tr>)}</tbody>
                  </table>
                )}
              </div>
            </>
          ) : <div className="card-flat p-8 text-center text-[#A1A1AA]">Select a client</div>}
        </div>
      </div>
    </div>
  );
};

// ==================== INVOICES PAGE ====================
const InvoicesPage = () => {
  const { selectedMonth, monthLabel } = useMonth();
  const [invoices, setInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    api.get("/invoices").then(r => { const all = r.data.invoices || []; setAllInvoices(all); setInvoices(all.filter(i => i.month === selectedMonth)); setLoading(false); }).catch(() => setLoading(false));
  }, [selectedMonth]);
  const displayInvoices = showAll ? allInvoices : invoices;
  return (
    <div data-testid="invoices-page">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="text-3xl font-bold">Invoices</h1><p className="text-[#52525B] mt-1">{showAll?"All months":monthLabel}</p></div>
        <button onClick={() => setShowAll(!showAll)} className="btn-secondary text-sm" data-testid="toggle-all-invoices">{showAll?`Show ${monthLabel} Only`:"Show All"}</button>
      </div>
      <div className="card-flat">
        {loading ? <div className="p-8 text-center text-[#A1A1AA]">Loading...</div> : displayInvoices.length === 0 ? <div className="p-8 text-center text-[#A1A1AA]">No invoices</div> : (
          <table className="table-dense"><thead><tr><th>Invoice #</th><th>Client</th><th>Month</th><th>Type</th><th className="text-right">Amount</th><th>Status</th><th>Payment</th></tr></thead>
            <tbody>{displayInvoices.map(inv => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className={inv.month===selectedMonth?"bg-[#1A1A00]":""}>
                <td className="font-medium">{inv.zoho_invoice_number||inv.id?.slice(0,8)}</td>
                <td><Link to={`/clients/${inv.client_id}`} className="hover:text-[#FFEB00]">{inv.client_id.slice(0,8)}</Link></td>
                <td>{inv.month}</td><td className="capitalize">{inv.invoice_type}</td>
                <td className="text-right data-number">{formatCurrency(inv.amount, inv.currency)}</td>
                <td><StatusBadge status={inv.status} /></td>
                <td><PaymentStatusBadge status={inv.payment_status || "unpaid"} /></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ==================== SETTINGS ====================
const SettingsPage = () => {
  const [zohoStatus, setZohoStatus] = useState(null);
  const [shopifyStores, setShopifyStores] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [facebookConn, setFacebookConn] = useState(null);
  const [crmConn, setCrmConn] = useState(null);
  const [gmailStatus, setGmailStatus] = useState(null);
  const [gmailScanStatus, setGmailScanStatus] = useState(null);
  const [gmailScanning, setGmailScanning] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.allSettled([api.get("/zoho/status"), api.get("/shopify/stores"), api.get("/scheduler/status"), api.get("/sync-logs?limit=5"), api.get("/facebook/connection"), api.get("/zoho-crm/connection"), api.get("/gmail/status")])
      .then(([z,s,sc,l,fb,crm,gm]) => {
        if (z.status === "fulfilled") setZohoStatus(z.value.data);
        if (s.status === "fulfilled") setShopifyStores(s.value.data.stores||[]);
        if (sc.status === "fulfilled") setSchedulerStatus(sc.value.data);
        if (l.status === "fulfilled") setSyncLogs(l.value.data.logs||[]);
        if (fb.status === "fulfilled") setFacebookConn(fb.value.data);
        if (crm.status === "fulfilled") setCrmConn(crm.value.data);
        if (gm.status === "fulfilled") setGmailStatus(gm.value.data);
        setLoading(false);
      });
    // On return from OAuth, refresh status
    const params = new URLSearchParams(window.location.search);
    if (params.get("gmail") === "connected") {
      setTimeout(() => api.get("/gmail/status").then(r => setGmailStatus(r.data)).catch(()=>{}), 500);
    }
  }, []);

  const connectFacebook = async () => {
    try {
      const res = await api.get("/facebook/auth-url");
      window.location.href = res.data.auth_url;
    } catch (err) { alert("Failed to get Facebook auth URL"); }
  };

  const disconnectFacebook = async () => {
    if (!window.confirm("Disconnect Facebook? Ad spend syncs will stop.")) return;
    await api.post("/facebook/disconnect");
    setFacebookConn({ connected: false });
  };

  const connectCRM = async () => {
    try {
      const res = await api.get("/zoho-crm/auth-url");
      window.location.href = res.data.auth_url;
    } catch (err) { alert("Failed to get CRM auth URL"); }
  };

  const disconnectCRM = async () => {
    if (!window.confirm("Disconnect Zoho CRM?")) return;
    await api.post("/zoho-crm/disconnect");
    setCrmConn({ connected: false });
  };

  const connectGmail = async () => {
    try {
      const res = await api.get("/gmail/auth-url?return_to=/settings");
      window.location.href = res.data.url;
    } catch (err) { alert("Failed to get Gmail auth URL"); }
  };

  const disconnectGmail = async () => {
    if (!window.confirm("Disconnect Gmail? Last-interaction dates will stop updating.")) return;
    await api.post("/gmail/disconnect");
    setGmailStatus({ connected: false, configured: true });
  };

  const runGmailScan = async () => {
    if (gmailScanning) return;
    setGmailScanning(true);
    try {
      await api.post("/gmail/scan", { days: 60, max_messages: 500 });
      const poll = setInterval(async () => {
        try {
          const r = await api.get("/gmail/scan-status");
          setGmailScanStatus(r.data.status);
          if (r.data.status && (r.data.status.status === "completed" || r.data.status.status === "error")) {
            clearInterval(poll);
            setGmailScanning(false);
          }
        } catch (e) { clearInterval(poll); setGmailScanning(false); }
      }, 2500);
    } catch (err) {
      setGmailScanning(false);
      alert(err.response?.data?.detail || "Scan failed to start");
    }
  };

  const [newsScanning, setNewsScanning] = useState(false);
  const [newsScanStatus, setNewsScanStatus] = useState(null);
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [bulkEnrichStatus, setBulkEnrichStatus] = useState(null);
  const [bulkTier, setBulkTier] = useState("");
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupApplying, setCleanupApplying] = useState(false);
  useEffect(() => {
    api.get("/rios/news/scan-status").then(r => setNewsScanStatus(r.data.status)).catch(() => {});
    api.get("/rios/bulk/enrich/status").then(r => setBulkEnrichStatus(r.data.status)).catch(() => {});
  }, []);

  const runNewsScan = async () => {
    if (newsScanning) return;
    setNewsScanning(true);
    try {
      const r = await api.post("/rios/news/scan", { include_linkedin: true });
      const jobId = r.data.job_id;
      const poll = setInterval(async () => {
        try {
          const s = await api.get(`/rios/news/scan-status?job_id=${jobId}`);
          setNewsScanStatus(s.data.status);
          if (s.data.status && (s.data.status.status === "completed" || s.data.status.status === "error")) {
            clearInterval(poll);
            setNewsScanning(false);
          }
        } catch (e) { clearInterval(poll); setNewsScanning(false); }
      }, 3000);
    } catch (err) {
      setNewsScanning(false);
      alert(err.response?.data?.detail || "News scan failed to start");
    }
  };

  const runBulkEnrich = async () => {
    if (bulkEnriching) return;
    const scope = bulkTier ? `Tier ${bulkTier} contacts only` : "up to 200 contacts";
    if (!window.confirm(`Start bulk Apollo enrichment for ${scope}?\n\nThis will consume Apollo credits (~1 per contact).`)) return;
    setBulkEnriching(true);
    try {
      const r = await api.post("/rios/bulk/enrich", { only_missing_fields: true, limit: 500, tier_filter: bulkTier || undefined });
      const jobId = r.data.job_id;
      const poll = setInterval(async () => {
        try {
          const s = await api.get(`/rios/bulk/enrich/status?job_id=${jobId}`);
          setBulkEnrichStatus(s.data.status);
          if (s.data.status && (s.data.status.status === "completed" || s.data.status.status === "error")) {
            clearInterval(poll);
            setBulkEnriching(false);
          }
        } catch (e) { clearInterval(poll); setBulkEnriching(false); }
      }, 4000);
    } catch (err) {
      setBulkEnriching(false);
      alert(err.response?.data?.detail || "Bulk enrich failed to start");
    }
  };

  const loadCleanupPreview = async () => {
    setCleanupLoading(true);
    try {
      const r = await api.get("/rios/bulk/clean-names/preview?limit=200");
      setCleanupPreview(r.data);
    } catch (err) {
      alert("Failed to load cleanup preview: " + (err.response?.data?.detail || err.message));
    } finally { setCleanupLoading(false); }
  };

  const applyCleanup = async (candidates) => {
    if (!candidates || !candidates.length) return;
    if (!window.confirm(`Apply ${candidates.length} name cleanups?`)) return;
    setCleanupApplying(true);
    try {
      const actions = candidates.map(c => ({
        contact_id: c.contact_id,
        new_name: c.suggested_full_name || c.current_name,
        new_company: c.suggested_company || c.current_company,
      }));
      const r = await api.post("/rios/bulk/clean-names/apply", { actions });
      alert(`Applied ${r.data.applied} cleanups. Errors: ${r.data.errors?.length || 0}`);
      // Remove accepted candidates from preview
      setCleanupPreview(prev => prev ? {
        ...prev,
        candidates: prev.candidates.filter(c => !candidates.find(a => a.contact_id === c.contact_id)),
        total_found: prev.candidates.filter(c => !candidates.find(a => a.contact_id === c.contact_id)).length,
      } : null);
    } catch (err) {
      alert("Apply failed: " + (err.response?.data?.detail || err.message));
    } finally { setCleanupApplying(false); }
  };

  const applySingleCleanup = async (c) => {
    setCleanupApplying(true);
    try {
      const action = {
        contact_id: c.contact_id,
        new_name: c.suggested_full_name || c.current_name,
        new_company: c.suggested_company || c.current_company,
      };
      await api.post("/rios/bulk/clean-names/apply", { actions: [action] });
      // Remove from preview
      setCleanupPreview(prev => prev ? {
        ...prev,
        candidates: prev.candidates.filter(x => x.contact_id !== c.contact_id),
        total_found: prev.candidates.filter(x => x.contact_id !== c.contact_id).length,
      } : null);
    } catch (err) {
      alert("Apply failed: " + (err.response?.data?.detail || err.message));
    } finally { setCleanupApplying(false); }
  };

  const rejectSingleCleanup = (c) => {
    setCleanupPreview(prev => prev ? {
      ...prev,
      candidates: prev.candidates.filter(x => x.contact_id !== c.contact_id),
      total_found: prev.candidates.filter(x => x.contact_id !== c.contact_id).length,
    } : null);
  };

  return (
    <div data-testid="settings-page">
      <div className="mb-8"><h1 className="text-3xl font-bold">Settings</h1></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="card-flat p-6"><h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><FileText size={24} /> Zoho Books</h3>
          {zohoStatus?.authenticated ? <div><div className="flex items-center gap-2 text-[#00C853] mb-2"><Check size={20} weight="bold" /> Connected</div>
            <p className="text-sm text-[#A1A1AA]">2 Organizations active</p></div>
          : <a href={`${API}/zoho/authorize`} className="btn-primary">Connect Zoho</a>}</div>
        <div className="card-flat p-6" data-testid="zoho-crm-card">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Users size={24} /> Zoho CRM</h3>
          {crmConn?.connected ? (
            <div>
              <div className="flex items-center gap-2 text-[#00C853] mb-2"><Check size={20} weight="bold" /> Connected</div>
              <p className="text-sm text-[#A1A1AA]">Contacts & Leads sync ready</p>
              <button onClick={disconnectCRM} className="btn-secondary text-sm text-[#FF4444] mt-3">Disconnect</button>
            </div>
          ) : (
            <div>
              <p className="text-sm text-[#A1A1AA] mb-3">Connect to sync CRM contacts & leads</p>
              <button onClick={connectCRM} className="btn-primary flex items-center gap-2" data-testid="connect-crm-btn">
                <Users size={18} /> Connect Zoho CRM
              </button>
            </div>
          )}
        </div>
        <div className="card-flat p-6" data-testid="gmail-card">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><EnvelopeSimple size={24} /> Gmail</h3>
          {gmailStatus?.connected ? (
            <div>
              <div className="flex items-center gap-2 text-[#00C853] mb-2"><Check size={20} weight="bold" /> Connected</div>
              <p className="text-sm text-[#A1A1AA] truncate">{gmailStatus.email}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={runGmailScan} disabled={gmailScanning} className="btn-primary text-sm" data-testid="gmail-scan-btn">
                  {gmailScanning ? "Scanning..." : "Scan Inbox (60d)"}
                </button>
                <button onClick={disconnectGmail} className="btn-secondary text-sm text-[#FF4444]" data-testid="disconnect-gmail-btn">Disconnect</button>
              </div>
              {gmailScanStatus && (
                <div className="mt-2">
                  <p className="text-xs text-[#A1A1AA]">
                    {gmailScanStatus.status === "running" && `Scanning… ${gmailScanStatus.processed || 0} processed, ${gmailScanStatus.matched || 0} matched`}
                    {gmailScanStatus.status === "completed" && `Done: ${gmailScanStatus.processed} processed, ${gmailScanStatus.updated_contacts || 0} contacts updated`}
                    {gmailScanStatus.status === "error" && `Error: ${gmailScanStatus.error || "unknown"}`}
                  </p>
                  {gmailScanStatus.status === "completed" && gmailScanStatus.updated_contact_list && gmailScanStatus.updated_contact_list.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-[#FFEB00] cursor-pointer hover:underline">
                        View {gmailScanStatus.updated_contact_list.length} updated contact{gmailScanStatus.updated_contact_list.length > 1 ? "s" : ""}
                      </summary>
                      <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                        {gmailScanStatus.updated_contact_list.map(c => (
                          <div key={c.contact_id} className="flex items-center justify-between text-xs p-2 bg-[#1A1A1A] border border-[#2A2A2A]">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{c.name}</p>
                              <p className="text-[#71717A] truncate">{c.email}{c.company_name ? ` · ${c.company_name}` : ""}</p>
                            </div>
                            <span className="text-[10px] text-[#00C853] ml-2 whitespace-nowrap">
                              {new Date(c.last_interaction_date).toLocaleDateString()} {c.direction === "sent" ? "↗" : "↘"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
          ) : gmailStatus && !gmailStatus.configured ? (
            <div><p className="text-sm text-[#FF4444]">OAuth not configured on server</p></div>
          ) : (
            <div>
              <p className="text-sm text-[#A1A1AA] mb-3">Connect Gmail to auto-detect last interaction date per contact</p>
              <button onClick={connectGmail} className="btn-primary flex items-center gap-2" data-testid="connect-gmail-btn">
                <EnvelopeSimple size={18} /> Connect Gmail
              </button>
            </div>
          )}
        </div>
        <div className="card-flat p-6" data-testid="news-signals-card">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><ChartLine size={24} /> News & LinkedIn Signals</h3>
          <p className="text-sm text-[#A1A1AA] mb-3">Scan Google News + public LinkedIn mentions for Tier A / Track Signals contacts</p>
          <button onClick={runNewsScan} disabled={newsScanning} className="btn-primary text-sm" data-testid="news-scan-btn">
            {newsScanning ? "Scanning..." : "Scan News Now"}
          </button>
          {newsScanStatus && (
            <p className="text-xs text-[#A1A1AA] mt-2">
              {newsScanStatus.status === "running" && `Scanning… ${newsScanStatus.entities_scanned || 0} entities, ${newsScanStatus.signals_found || 0} signals`}
              {newsScanStatus.status === "completed" && `Last run: ${newsScanStatus.entities_scanned || 0} entities scanned, ${newsScanStatus.signals_found || 0} new signals`}
              {newsScanStatus.status === "error" && `Error: ${newsScanStatus.error || "unknown"}`}
            </p>
          )}
        </div>
        <div className="card-flat p-6" data-testid="bulk-enrich-card">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Sparkle size={24} /> Bulk Enrich</h3>
          <p className="text-sm text-[#A1A1AA] mb-3">Enrich contacts via Apollo. Auto-fills designation, LinkedIn, phone, etc. for contacts with missing fields.</p>
          <div className="flex items-center gap-2 mb-3">
            <label className="text-xs text-[#71717A]">Scope:</label>
            <select
              value={bulkTier}
              onChange={e => setBulkTier(e.target.value)}
              className="input-default text-xs py-1"
              data-testid="bulk-enrich-tier-select"
            >
              <option value="">All Tiers</option>
              <option value="A">Tier A only</option>
              <option value="B">Tier B only</option>
              <option value="C">Tier C only</option>
            </select>
          </div>
          <button onClick={runBulkEnrich} disabled={bulkEnriching} className="btn-primary text-sm" data-testid="bulk-enrich-btn">
            {bulkEnriching ? "Enriching..." : "Start Bulk Enrich"}
          </button>
          {bulkEnrichStatus && (
            <p className="text-xs text-[#A1A1AA] mt-2">
              {bulkEnrichStatus.tier_filter && <span className="text-[#FFEB00]">Tier {bulkEnrichStatus.tier_filter} · </span>}
              {bulkEnrichStatus.status === "running" && `Running… ${bulkEnrichStatus.processed || 0} / ${bulkEnrichStatus.total || "?"} processed, ${bulkEnrichStatus.auto_applied || 0} updated`}
              {bulkEnrichStatus.status === "completed" && `Last run: ${bulkEnrichStatus.processed} processed, ${bulkEnrichStatus.auto_applied} contacts updated`}
              {bulkEnrichStatus.status === "error" && `Error: ${bulkEnrichStatus.error || "unknown"}`}
            </p>
          )}
        </div>
        <div className="card-flat p-6" data-testid="cleanup-card">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Broom size={24} /> Clean Contact Names</h3>
          <p className="text-sm text-[#A1A1AA] mb-3">Find contacts where "name" is actually a company and split into proper name + company.</p>
          <button onClick={loadCleanupPreview} disabled={cleanupLoading} className="btn-primary text-sm" data-testid="cleanup-preview-btn">
            {cleanupLoading ? "Scanning..." : "Preview Cleanup"}
          </button>
        </div>
        <div className="card-flat p-6"><h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Storefront size={24} /> Shopify ({shopifyStores.length})</h3>
          <div className="max-h-48 overflow-y-auto space-y-1">{shopifyStores.map(s => <div key={s.shop} className="text-sm p-2 bg-[#1A1A1A] border border-[#2A2A2A]">{s.shop}</div>)}</div></div>
        <div className="card-flat p-6" data-testid="facebook-connection-card">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><ChartLine size={24} /> Facebook Ads</h3>
          {facebookConn?.connected ? (
            <div>
              <div className="flex items-center gap-2 text-[#00C853] mb-2"><Check size={20} weight="bold" /> Connected</div>
              <p className="text-sm text-[#A1A1AA]">{facebookConn.user_name} ({facebookConn.user_email})</p>
              <p className="text-sm text-[#A1A1AA] mt-1">{facebookConn.ad_accounts?.length || 0} ad accounts</p>
              {facebookConn.expired && <p className="text-sm text-[#FF4444] mt-1">Token expired — reconnect</p>}
              <div className="flex gap-2 mt-3">
                {facebookConn.expired && <button onClick={connectFacebook} className="btn-primary text-sm" data-testid="reconnect-facebook-btn">Reconnect</button>}
                <button onClick={disconnectFacebook} className="btn-secondary text-sm text-[#FF4444]" data-testid="disconnect-facebook-btn">Disconnect</button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm text-[#A1A1AA] mb-3">Connect to auto-sync ad spends and calculate ROAS</p>
              <button onClick={connectFacebook} className="btn-primary flex items-center gap-2" data-testid="connect-facebook-btn">
                <ChartLine size={18} /> Connect Facebook
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card-flat p-6" data-testid="scheduler-card"><h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Timer size={24} /> Daily Sync</h3>
          {schedulerStatus && <div className="space-y-2">
            <div className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${schedulerStatus.running?"bg-[#00C853]":"bg-[#FF4444]"}`}></span>{schedulerStatus.running?"Running":"Stopped"}</div>
            {schedulerStatus.jobs?.map(j => <p key={j.id} className="text-sm text-[#A1A1AA]">Next: {new Date(j.next_run).toLocaleString()}</p>)}
            <button onClick={() => api.post("/scheduler/trigger-sync").then(() => alert("Sync triggered!"))} className="btn-secondary text-sm mt-2" data-testid="trigger-sync-btn">Trigger Now</button>
          </div>}</div>
        <div className="card-flat p-6" data-testid="sync-logs-card"><h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><ArrowsClockwise size={24} /> Sync Logs</h3>
          {syncLogs.length === 0 ? <p className="text-[#A1A1AA]">No logs</p> : syncLogs.map((l,i) => (
            <div key={i} className="text-sm p-2 bg-[#1A1A1A] border border-[#2A2A2A] mb-1">
              <div className="flex justify-between"><span className="text-[#FFEB00]">{l.month}</span><span className="text-[#A1A1AA]">{new Date(l.run_at).toLocaleString()}</span></div>
              <p className="text-[#A1A1AA]">{l.success} synced, {l.skipped} skipped, {l.failed} failed</p>
            </div>
          ))}</div>
      </div>
      {cleanupPreview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="cleanup-modal">
          <div className="card-flat max-w-4xl w-full max-h-[85vh] flex flex-col">
            <div className="p-4 border-b border-[#2A2A2A] flex items-center justify-between">
              <h3 className="font-semibold">Contact Name Cleanup — {cleanupPreview.total_found} candidates</h3>
              <button onClick={() => setCleanupPreview(null)} className="btn-secondary text-sm">Close</button>
            </div>
            <div className="overflow-y-auto flex-1 p-4 space-y-2">
              {cleanupPreview.candidates.map(c => (
                <div key={c.contact_id} className={`p-3 border ${c.confidence === "high" ? "border-[#00C853]/30" : c.confidence === "medium" ? "border-[#FFEB00]/30" : "border-[#71717A]/30"}`}>
                  <div className="text-xs text-[#71717A] mb-1 flex items-center justify-between">
                    <span>email: {c.current_email || "(none)"} · confidence: {c.confidence} · action: {c.action}</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => applySingleCleanup(c)}
                        disabled={cleanupApplying}
                        className="btn-primary text-[10px] px-2 py-1"
                        data-testid={`cleanup-accept-${c.contact_id}`}
                      >Accept</button>
                      <button
                        onClick={() => rejectSingleCleanup(c)}
                        className="btn-secondary text-[10px] px-2 py-1"
                        data-testid={`cleanup-reject-${c.contact_id}`}
                      >Reject</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-[10px] text-[#71717A]">CURRENT</p>
                      <p>name: <span className="text-[#A1A1AA]">{c.current_name || "(empty)"}</span></p>
                      <p>company: <span className="text-[#A1A1AA]">{c.current_company || "(empty)"}</span></p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#FFEB00]">SUGGESTED</p>
                      <p>name: <span className="text-[#FFEB00]">{c.suggested_full_name || "(leave as-is)"}</span></p>
                      <p>company: <span className="text-[#FFEB00]">{c.suggested_company}</span></p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-[#2A2A2A] flex gap-2 justify-end">
              <button
                onClick={() => applyCleanup(cleanupPreview.candidates.filter(c => c.confidence === "high"))}
                disabled={cleanupApplying}
                className="btn-secondary text-sm"
                data-testid="cleanup-apply-high"
              >Apply High-Confidence Only</button>
              <button
                onClick={() => applyCleanup(cleanupPreview.candidates)}
                disabled={cleanupApplying}
                className="btn-primary text-sm"
                data-testid="cleanup-apply-all"
              >Apply All {cleanupPreview.candidates.length}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const AuthCallback = () => {
  const { login, checkAuth } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");
    if (!sessionId) { navigate("/login", { replace: true }); return; }

    // Clear hash immediately to prevent re-processing
    window.history.replaceState(null, "", window.location.pathname);

    axios.post(`${API}/auth/google/session`, { session_id: sessionId })
      .then(r => {
        if (r.data.success) {
          login(r.data.user, r.data.session_token);
          // Small delay to ensure localStorage is written before navigation
          setTimeout(() => navigate("/", { replace: true }), 100);
        } else { navigate("/login", { replace: true }); }
      })
      .catch((err) => {
        console.error("Auth callback error:", err);
        navigate("/login", { replace: true });
      });
  }, [login, navigate, checkAuth]);

  return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><p className="text-[#71717A]">Signing you in...</p></div>;
};

// ==================== RIOS PAGE (sub-routing) ====================
const RIOSPage = () => {
  const [view, setView] = useState("dashboard");
  const [selectedContactId, setSelectedContactId] = useState(null);

  if (selectedContactId) {
    return <ContactProfile contactId={selectedContactId} onBack={() => setSelectedContactId(null)} />;
  }

  return (
    <div>
      <div className="flex border-b border-[#2A2A2A] bg-[#0A0A0A] sticky top-0 z-10">
        {[["dashboard", "Dashboard"], ["contacts", "Contacts"], ["companies", "Companies"], ["brands", "Brands"], ["import", "CSV Import"]].map(([key, label]) => (
          <button key={key} onClick={() => setView(key)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${view === key ? "border-[#FFEB00] text-[#FFEB00]" : "border-transparent text-[#71717A] hover:text-white"}`}
            data-testid={`rios-tab-${key}`}>{label}</button>
        ))}
      </div>
      {view === "dashboard" && <RIOSDashboard onSelectContact={setSelectedContactId} />}
      {view === "contacts" && <ContactsList onSelectContact={setSelectedContactId} />}
      {view === "companies" && <CompaniesList />}
      {view === "brands" && <BrandsList />}
      {view === "import" && <CSVImport />}
    </div>
  );
};

// ==================== ADMIN PAGE ====================
const AdminPage = () => {
  const { user } = useAuth();
  const [adminTab, setAdminTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("restricted");
  const [newPerms, setNewPerms] = useState(["revenue"]);
  const [managingUser, setManagingUser] = useState(null);
  const [assignDraft, setAssignDraft] = useState([]);
  const [clientSearch, setClientSearch] = useState("");

  const refreshUsers = async () => {
    const r = await axios.get(`${API}/auth/admin/users`);
    setUsers(r.data.users || []);
  };

  useEffect(() => {
    refreshUsers().catch(() => {});
    axios.get(`${API}/clients?include_inactive=true`).then(r => setAllClients(r.data.clients || [])).catch(() => {});
  }, []);

  const addUser = async () => {
    if (!newEmail) return;
    // admin_user and client_user should NOT have revenue/rios permissions
    // (both are scoped to assigned_client_ids). Force empty perms.
    const permsToSend = (newRole === "admin_user" || newRole === "client_user") ? [] : newPerms;
    await axios.post(`${API}/auth/admin/users`, { email: newEmail, role: newRole, permissions: permsToSend });
    setNewEmail(""); setShowAdd(false);
    await refreshUsers();
  };

  const togglePerm = async (userId, perm, currentPerms) => {
    const updated = currentPerms.includes(perm) ? currentPerms.filter(p => p !== perm) : [...currentPerms, perm];
    await axios.put(`${API}/auth/admin/users/${userId}`, { permissions: updated });
    await refreshUsers();
  };

  const setUserRole = async (userId, role) => {
    await axios.put(`${API}/auth/admin/users/${userId}`, { role });
    await refreshUsers();
  };

  const openManageClients = (u) => {
    setManagingUser(u);
    setAssignDraft(u.assigned_client_ids || []);
    setClientSearch("");
  };

  const saveAssignedClients = async () => {
    await axios.put(`${API}/auth/admin/users/${managingUser.user_id}`, { assigned_client_ids: assignDraft });
    setManagingUser(null);
    await refreshUsers();
  };

  const toggleAssign = (cid) => {
    setAssignDraft(prev => prev.includes(cid) ? prev.filter(x => x !== cid) : [...prev, cid]);
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Delete this user?")) return;
    await axios.delete(`${API}/auth/admin/users/${userId}`);
    setUsers(prev => prev.filter(u => u.user_id !== userId));
  };

  if (user?.role !== "super_admin") return <div className="p-8 text-red-400">Access denied</div>;

  const filteredClients = allClients.filter(c =>
    !clientSearch || ((c.client_name || c.name || "") + " " + (c.project_name || "")).toLowerCase().includes(clientSearch.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6" data-testid="admin-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Varela Round" }}>Admin</h1>
        {adminTab === "users" && (
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/auth/admin/users/export.csv`, { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `revflow_users_${new Date().toISOString().slice(0, 10)}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch (e) {
                  alert(e.response?.data?.detail || e.message || "Export failed");
                }
              }}
              className="btn-secondary flex items-center gap-2 text-sm"
              data-testid="export-users-csv-btn"
              title="Download the full user list as CSV"
            >
              <FileText size={16} /> Export CSV
            </button>
            <button onClick={() => setShowAdd(!showAdd)} className="btn-primary flex items-center gap-2" data-testid="admin-add-toggle"><Plus size={16} /> Add User</button>
          </div>
        )}
      </div>

      <div className="tabs" data-testid="admin-tabs">
        <button onClick={() => setAdminTab("users")} className={`tab ${adminTab === "users" ? "active" : ""}`} data-testid="admin-tab-users">Users</button>
        <button onClick={() => setAdminTab("branding")} className={`tab ${adminTab === "branding" ? "active" : ""}`} data-testid="admin-tab-branding">Branding</button>
      </div>

      {adminTab === "branding" && (
        <BrandingEditor scope="agency" />
      )}

      {adminTab === "users" && <>
      {showAdd && (
        <div className="card-flat p-5 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <input className="input-default" placeholder="Email" value={newEmail} onChange={e => setNewEmail(e.target.value)} data-testid="admin-new-email" />
            <select className="input-default" value={newRole} onChange={e => setNewRole(e.target.value)} data-testid="admin-new-role">
              <option value="restricted">Restricted</option>
              <option value="client_user">Client User</option>
              <option value="admin_user">Admin User</option>
              <option value="super_admin">Super Admin</option>
            </select>
            {/* Revenue/RIOS are super_admin-tier cross-client permissions.
                They are ignored for client_user / admin_user (both are scoped
                to assigned_client_ids). Hide them to remove the confusion. */}
            {(newRole === "super_admin" || newRole === "restricted") ? (
              <div className="flex items-center gap-3" data-testid="admin-new-perms">
                <label className="flex items-center gap-1 text-sm cursor-pointer">
                  <input type="checkbox" checked={newPerms.includes("revenue")} onChange={() => setNewPerms(p => p.includes("revenue") ? p.filter(x => x !== "revenue") : [...p, "revenue"])} className="accent-[#FFEB00]" data-testid="admin-new-perm-revenue" />
                  Revenue
                </label>
                <label className="flex items-center gap-1 text-sm cursor-pointer">
                  <input type="checkbox" checked={newPerms.includes("rios")} onChange={() => setNewPerms(p => p.includes("rios") ? p.filter(x => x !== "rios") : [...p, "rios"])} className="accent-[#FFEB00]" data-testid="admin-new-perm-rios" />
                  RIOS
                </label>
              </div>
            ) : (
              <div className="text-[11px] text-[#71717A] italic self-center" data-testid="admin-new-perms-scoped-note">
                Scoped by assigned clients — no global toggles apply.
              </div>
            )}
          </div>
          <p className="text-[11px] text-[#71717A]">
            <strong>Client User</strong> = read-only access scoped to specific clients (Performance Flow + Intelligence only). <strong>Admin User</strong> = operational role that can configure Performance integrations, branding & spend mapping for assigned clients only — <em>zero access</em> to billing, invoices, RIOS or other clients&apos; financials. <strong>Revenue</strong> / <strong>RIOS</strong> checkboxes only apply to Super Admin &amp; Restricted (global cross-client access). For Client User / Admin User, assign specific clients via &quot;Manage Clients&quot; after creation.
          </p>
          <button onClick={addUser} className="btn-primary text-sm" data-testid="admin-add-submit">Add User</button>
        </div>
      )}
      <table className="table-dense w-full">
        <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Revenue</th><th>RIOS</th><th>Clients</th><th></th></tr></thead>
        <tbody>
          {users.map(u => (
            <tr key={u.user_id}>
              <td className="font-medium text-sm">{u.email}</td>
              <td className="text-sm">{u.name}</td>
              <td>
                <select
                  value={u.role || "restricted"}
                  onChange={(e) => setUserRole(u.user_id, e.target.value)}
                  className="bg-[#1A1A1A] border border-[#2A2A2A] text-xs px-2 py-0.5 rounded text-white"
                  data-testid={`admin-role-${u.user_id}`}
                >
                  <option value="restricted">Restricted</option>
                  <option value="client_user">Client User</option>
                  <option value="admin_user">Admin User</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </td>
              <td>
                {(u.role === "client_user" || u.role === "admin_user") ? (
                  <span className="text-[10px] text-[#52525B] italic" title="Not applicable — scoped roles use assigned clients instead">n/a</span>
                ) : (
                  <input type="checkbox" checked={u.permissions?.includes("revenue")} onChange={() => togglePerm(u.user_id, "revenue", u.permissions || [])} className="accent-[#FFEB00]" data-testid={`admin-perm-revenue-${u.user_id}`} />
                )}
              </td>
              <td>
                {(u.role === "client_user" || u.role === "admin_user") ? (
                  <span className="text-[10px] text-[#52525B] italic" title="Not applicable — scoped roles use assigned clients instead">n/a</span>
                ) : (
                  <input type="checkbox" checked={u.permissions?.includes("rios")} onChange={() => togglePerm(u.user_id, "rios", u.permissions || [])} className="accent-[#FFEB00]" data-testid={`admin-perm-rios-${u.user_id}`} />
                )}
              </td>
              <td>
                <button
                  onClick={() => openManageClients(u)}
                  className="text-xs px-2 py-0.5 rounded border border-[#3A3A3A] text-[#A1A1AA] hover:text-white"
                  data-testid={`admin-manage-clients-${u.user_id}`}
                >
                  {(u.assigned_client_ids?.length || 0)} client{(u.assigned_client_ids?.length || 0) === 1 ? "" : "s"} · Manage
                </button>
              </td>
              <td>{u.role !== "super_admin" && <button onClick={() => deleteUser(u.user_id)} className="text-[#FF4444] hover:text-[#FF6666] text-xs">Remove</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {managingUser && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="manage-clients-modal">
          <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-[#2A2A2A] flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-white">Assign clients to {managingUser.email}</h3>
                <p className="text-[11px] text-[#71717A]">Selected: {assignDraft.length} of {allClients.length}</p>
              </div>
              <button onClick={() => setManagingUser(null)} className="text-[#71717A] hover:text-white text-sm" data-testid="manage-clients-close">✕</button>
            </div>
            <div className="p-4 border-b border-[#2A2A2A] flex items-center gap-2">
              <input
                className="input-default flex-1"
                placeholder="Search clients…"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                data-testid="manage-clients-search"
              />
              <button onClick={() => setAssignDraft(filteredClients.map(c => c.id))} className="text-[11px] px-2 py-1 rounded border border-[#3A3A3A] text-[#A1A1AA] hover:text-white">Select all (filtered)</button>
              <button onClick={() => setAssignDraft([])} className="text-[11px] px-2 py-1 rounded border border-[#3A3A3A] text-[#A1A1AA] hover:text-white">Clear</button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {filteredClients.map(c => (
                <label
                  key={c.id}
                  className="flex items-center gap-2 p-2 hover:bg-[#1A1A1A] rounded cursor-pointer text-sm"
                  data-testid={`assign-client-${c.id}`}
                >
                  <input
                    type="checkbox"
                    checked={assignDraft.includes(c.id)}
                    onChange={() => toggleAssign(c.id)}
                    className="accent-[#FFEB00]"
                  />
                  <span className="text-white">{c.client_name || c.name || c.id}</span>
                  {c.project_name && <span className="text-[10px] text-[#71717A]">· {c.project_name}</span>}
                  <span className="ml-auto text-[10px] text-[#71717A] uppercase">{c.status}</span>
                </label>
              ))}
              {filteredClients.length === 0 && <div className="text-xs text-[#71717A] p-4">No matching clients.</div>}
            </div>
            <div className="p-4 border-t border-[#2A2A2A] flex justify-end gap-2">
              <button onClick={() => setManagingUser(null)} className="px-3 py-1.5 text-xs rounded border border-[#3A3A3A] text-[#A1A1AA]">Cancel</button>
              <button onClick={saveAssignedClients} className="btn-primary text-xs px-3 py-1.5" data-testid="manage-clients-save">Save</button>
            </div>
          </div>
        </div>
      )}
      </>}
    </div>
  );
};

// ==================== APP ====================
function RIOSGuard({ children }) {
  const { hasPermission } = useAuth();
  if (!hasPermission("rios")) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]" data-testid="rios-no-access">
        <div className="text-center p-8 max-w-md">
          <h2 className="text-xl font-semibold text-white mb-2">No access to Relationship OS</h2>
          <p className="text-sm text-[#71717A]">Your account doesn't have the RIOS permission. Contact a super-admin if you need access.</p>
        </div>
      </div>
    );
  }
  return children;
}

function InternalOnlyGuard({ children }) {
  // client_user is a read-only client portal role — block them from any
  // internal agency route (billing, invoices, ad-spend, settings, admin,
  // client editing). Their UI is the scoped Dashboard + Clients list/detail.
  const { user } = useAuth();
  if (user?.role === "client_user") {
    return <Navigate to="/" replace />;
  }
  return children;
}


function AppRouter() {
  const location = useLocation();
  const { user, loading } = useAuth();

  // CRITICAL: Detect session_id before any other routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><p className="text-[#71717A]">Loading...</p></div>;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <div className="App">
      <AppShell />
    </div>
  );
}

// Layout shell that owns mobile-drawer state + closes it on route change
function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  const isClientUser = user?.role === "client_user";
  const isAuthRoute = location.pathname === "/login" || location.pathname === "/privacy";

  return (
    <>
      <MobileTopbar onOpen={() => setDrawerOpen(true)} />
      <Sidebar mobileOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />
      {drawerOpen && (
        <div
          className="mobile-backdrop"
          onClick={() => setDrawerOpen(false)}
          data-testid="mobile-backdrop"
        />
      )}
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/clients" element={<ClientsList />} />
          <Route path="/clients/new" element={<InternalOnlyGuard><ClientForm /></InternalOnlyGuard>} />
          <Route path="/clients/:id" element={<ClientDetail />} />
          <Route path="/clients/:id/edit" element={<InternalOnlyGuard><ClientForm /></InternalOnlyGuard>} />
          <Route path="/clients/:clientId/rules" element={<InternalOnlyGuard><RulesConfiguration /></InternalOnlyGuard>} />
          <Route path="/ad-spend" element={<InternalOnlyGuard><AdSpendEntry /></InternalOnlyGuard>} />
          <Route path="/billing" element={<InternalOnlyGuard><BillingPage /></InternalOnlyGuard>} />
          <Route path="/billing-trends" element={<InternalOnlyGuard><BillingTrends /></InternalOnlyGuard>} />
          <Route path="/billing/:clientId/calculate" element={<InternalOnlyGuard><BillingCalculator /></InternalOnlyGuard>} />
          <Route path="/billing/:clientId/:month" element={<InternalOnlyGuard><BillingCalculator /></InternalOnlyGuard>} />
          <Route path="/invoices" element={<InternalOnlyGuard><InvoicesPage /></InternalOnlyGuard>} />
          <Route path="/settings" element={<InternalOnlyGuard><SettingsPage /></InternalOnlyGuard>} />
          <Route path="/rios" element={<RIOSGuard><RIOSPage /></RIOSGuard>} />
          <Route path="/admin" element={<InternalOnlyGuard><AdminPage /></InternalOnlyGuard>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Routes>
        {isClientUser && !isAuthRoute && <DiBrandFooter />}
      </main>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <BrandProvider>
            <MonthProvider>
              <AppRouter />
            </MonthProvider>
          </BrandProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
